import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

import { Avatar, Card, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Grid, IconButton, InputAdornment, LinearProgress, MenuItem, Skeleton, Tooltip } from "@mui/material";
// Büyük formda her tuşta tüm formun yeniden çizilmesini önleyen tamponlu TextField (yazma gecikmesi)
import TextField from "components/BufferedTextField";

import {
    AccountBalanceWallet,
    ArrowUpward,
    CheckCircle,
    Download,
    DirectionsBoat,
    Email,
    Flight,
    EventAvailable,
    FilterAlt,
    LocalShipping,
    Notes,
    Paid,
    ReceiptLong,
    Refresh,
    RestartAlt,
    Search,
    Visibility,
} from "@mui/icons-material";

import FinanceStatusChip from "../components/FinanceStatusChip";
import { formatCurrency, formatDate } from "../utils/formatter";
import {
    COLORS,
    cardSx,
    FinanceCard,
    FinanceSectionHeader,
    FinanceKpiTile,
    FinanceHeroStat,
    SPACE,
    FinanceDetailsSection,
} from "../shared/FinanceUiKit";
import { getVatAmount, hasVatIncluded } from "../shared/vatDisplay";

import {
    financeText,
    getFinanceLanguage,
    saveFinanceLanguage,
} from "finance-language";
import { appAlert } from "components/AppDialog";
// Koyu hero bandı için (yedekten geri yüklendi)
import { WorkspacePremium } from "@mui/icons-material";
import { CalendarMonth } from "@mui/icons-material";

const API_URL = process.env.REACT_APP_API_URL;

const getValue = (row, camel, snake, fallback = "") =>
    row?.[camel] ?? row?.[snake] ?? fallback;

const toNumber = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
};

const firstDefinedNumber = (...values) => {
    const value = values.find(
        (item) => item !== undefined && item !== null && item !== "",
    );
    return toNumber(value);
};

const getBuyingRate = (item) =>
    firstDefinedNumber(
        item?.buyingRate,
        item?.buying_rate,
        item?.financialSummary?.buyingRate,
        item?.financial_summary?.buying_rate,
        item?.freightCharges?.buyingRate,
        item?.freight_charges?.buying_rate,
    );

const getOperationalCost = (item) => {
    const summary = item?.financialSummary || item?.financial_summary || {};
    const freightCharges = item?.freightCharges || item?.freight_charges || {};

    const explicitOperationalCost = firstDefinedNumber(
        item?.operationalCost,
        item?.operational_cost,
        summary?.operationalCost,
        summary?.operational_cost,
    );

    if (explicitOperationalCost > 0) return explicitOperationalCost;

    const roadCost =
        firstDefinedNumber(summary?.shipmentCostTotal) +
        firstDefinedNumber(summary?.convertedLinehaulCost) +
        firstDefinedNumber(summary?.convertedHandlingCost);

    const oceanCost =
        firstDefinedNumber(summary?.pickupTotal) +
        firstDefinedNumber(summary?.othcTotal) +
        firstDefinedNumber(summary?.oceanFreightTotal) +
        firstDefinedNumber(summary?.dthcTotal) +
        firstDefinedNumber(summary?.handlingTotal) +
        firstDefinedNumber(summary?.deliveryTotal);

    const airCost =
        firstDefinedNumber(summary?.pickupTotal) +
        firstDefinedNumber(summary?.othcTotal) +
        firstDefinedNumber(summary?.airFreightTotal) +
        firstDefinedNumber(summary?.dthcTotal) +
        firstDefinedNumber(summary?.handlingTotal) +
        firstDefinedNumber(summary?.deliveryTotal);

    const freightChargeCost =
        firstDefinedNumber(freightCharges?.pickupTotal) +
        firstDefinedNumber(freightCharges?.othc?.total) +
        firstDefinedNumber(freightCharges?.oceanFreight?.total) +
        firstDefinedNumber(freightCharges?.airFreight?.total) +
        firstDefinedNumber(freightCharges?.dthc?.total) +
        firstDefinedNumber(freightCharges?.handling?.total) +
        firstDefinedNumber(freightCharges?.deliveryTotal);

    if (roadCost > 0) return roadCost;
    if (oceanCost > 0) return oceanCost;
    if (airCost > 0) return airCost;
    if (freightChargeCost > 0) return freightChargeCost;

    return 0;
};

const getTotalCost = (item) => {
    const explicitTotalCost = firstDefinedNumber(
        item?.actualCostTotal,
        item?.actual_cost_total,
        item?.totalCost,
        item?.total_cost,
        item?.financialSummary?.totalCost,
        item?.financial_summary?.total_cost,
    );

    const invoiceItems =
        [item?.invoiceItems, item?.invoice_items].find(Array.isArray) || [];
    const invoiceItemCost = invoiceItems.length
        ? invoiceItems.reduce(
            (sum, invoiceItem) =>
                sum +
                firstDefinedNumber(
                    invoiceItem?.totalCost,
                    invoiceItem?.total_cost,
                    invoiceItem?.cost,
                    invoiceItem?.unitCost,
                    invoiceItem?.unit_cost,
                ),
            0,
        )
        : 0;

    if (explicitTotalCost > 0) {
        // Backend totals are authoritative. Adding buyingRate here would make
        // the same freight cost appear twice in dashboard totals.
        return explicitTotalCost;
    }

    if (invoiceItemCost > 0) return invoiceItemCost;

    return getOperationalCost(item) + getBuyingRate(item);
};

const getProfit = (item) => getExVatInvoiceAmount(item) - getTotalCost(item);

const getMargin = (item) => {
    const revenue = getExVatInvoiceAmount(item);
    return revenue > 0 ? (getProfit(item) / revenue) * 100 : 0;
};

const normalizeCurrencyCode = (currency) => {
    const code = String(currency || "EUR")
        .trim()
        .toUpperCase();
    if (code === "EURO") return "EUR";
    if (code === "TL") return "TRY";
    return code;
};

const getCurrency = (item) =>
    normalizeCurrencyCode(
        item?.sellingRateCurrency ||
        item?.selling_rate_currency ||
        item?.currency ||
        item?.invoiceCurrency ||
        item?.invoice_currency ||
        item?.financialSummary?.sellingRateCurrency ||
        item?.financial_summary?.selling_rate_currency ||
        item?.financialSummary?.totalCurrency ||
        item?.financial_summary?.total_currency ||
        item?.freightCharges?.totalCurrency ||
        item?.freight_charges?.total_currency ||
        "EUR",
    );

const getCostCurrency = (item) => {
    const items =
        [
            item?.invoiceItems,
            item?.invoice_items,
            item?.cargoDetails,
            item?.cargo_details,
        ].find(Array.isArray) || [];
    const itemWithCurrency = items.find(
        (invoiceItem) => invoiceItem?.costCurrency || invoiceItem?.cost_currency,
    );
    const itemCurrency =
        itemWithCurrency?.costCurrency || itemWithCurrency?.cost_currency;

    return normalizeCurrencyCode(
        item?.costCurrency ||
        item?.cost_currency ||
        item?.totalCostCurrency ||
        item?.total_cost_currency ||
        itemCurrency ||
        item?.financialSummary?.costCurrency ||
        item?.financial_summary?.cost_currency ||
        item?.financialSummary?.totalCurrency ||
        item?.financial_summary?.total_currency ||
        "EUR",
    );
};

// VAT-exclusive selling rate: the revenue figure profit and margin use.
const getExVatInvoiceAmount = (item) =>
    firstDefinedNumber(
        item?.invoiceSalesAmount,
        item?.salesAmount,
        item?.customerInvoiceAmount,
        item?.totalAmount,
        item?.total_amount,
        item?.amount,
    );

// What the customer is billed: VAT included when the invoice carries VAT.
// Receivable balance, gross/net amounts and collection status use this.
const getInvoiceAmount = (item) =>
    hasVatIncluded(item)
        ? firstDefinedNumber(item?.sellingRateInclVat)
        : getExVatInvoiceAmount(item);

const getPaidAmount = (item) =>
    firstDefinedNumber(
        item?.paidAmount,
        item?.paid_amount,
        item?.collectedAmount,
        item?.collected_amount,
    );

const getEntityId = (value) => {
    if (!value) return "";
    if (typeof value === "object") {
        return getEntityId(value?._id || value?.id || value?.$oid);
    }
    return String(value).trim();
};

const getFinanceJobKey = (item, index = 0) =>
    getEntityId(
        item?.financeJobId ||
        item?.finance_job_id ||
        item?.jobFinanceId ||
        item?.job_finance_id ||
        item?.shipmentId ||
        item?.shipment_id,
    ) || `invoice-${getEntityId(item?._id || item?.id) || index}`;

const getReturnStatus = (item) =>
    String(item?.returnStatus || item?.return_status || "Draft").trim();

const getOriginalInvoiceId = (item) =>
    getEntityId(
        item?.originalInvoiceId ||
        item?.original_invoice_id ||
        item?.originalInvoice?._id ||
        item?.originalInvoice?.id,
    );

const getOriginalInvoiceNo = (item) =>
    String(
        item?.originalInvoiceNumber ||
        item?.original_invoice_number ||
        item?.originalInvoiceNo ||
        item?.original_invoice_no ||
        item?.originalInvoice?.invoiceNo ||
        item?.originalInvoice?.invoice_no ||
        "",
    ).trim();

const getReturnInvoiceNo = (item) =>
    String(
        item?.returnInvoiceNo ||
        item?.return_invoice_no ||
        item?.invoiceNo ||
        item?.invoice_no ||
        item?._id ||
        "N/A",
    ).trim();

const getReturnInvoiceAmount = (item) =>
    Math.abs(
        firstDefinedNumber(
            item?.returnedAmount,
            item?.returned_amount,
            item?.returnAmount,
            item?.return_amount,
            item?.returnedTotalWithVat,
            item?.returned_total_with_vat,
            item?.totalWithVat,
            item?.total_with_vat,
            item?.totalAmount,
            item?.total_amount,
            item?.invoiceSalesAmount,
            item?.amount,
        ),
    );

const getLinkedReturnInvoices = (item) =>
    [
        item?.returnInvoices,
        item?.return_invoices,
        item?.linkedReturnInvoices,
        item?.linked_return_invoices,
    ].find(Array.isArray) || [];

const getReturnDate = (item) => {
    const raw =
        item?.returnDate ||
        item?.return_date ||
        item?.invoiceDate ||
        item?.invoice_date ||
        item?.createdAt ||
        item?.created_at ||
        item?.updatedAt ||
        item?.updated_at;
    const date = raw ? new Date(raw) : null;
    return date && !Number.isNaN(date.getTime()) ? date.getTime() : 0;
};

const getReturnSummary = (item) => {
    const allReturns = getLinkedReturnInvoices(item);
    const activeReturns = allReturns.filter(
        (returnInvoice) =>
            getReturnStatus(returnInvoice).toLowerCase() !== "cancelled",
    );
    const issuedReturns = activeReturns.filter(
        (returnInvoice) =>
            getReturnStatus(returnInvoice).toLowerCase() === "issued",
    );
    const latestActiveReturn =
        [...activeReturns].sort((a, b) => getReturnDate(b) - getReturnDate(a))[0] ||
        null;
    const latestReturnInvoice =
        [...allReturns].sort((a, b) => getReturnDate(b) - getReturnDate(a))[0] ||
        null;
    const issuedAmount = issuedReturns.reduce(
        (sum, returnInvoice) => sum + getReturnInvoiceAmount(returnInvoice),
        0,
    );
    const reservedAmount = activeReturns.reduce(
        (sum, returnInvoice) => sum + getReturnInvoiceAmount(returnInvoice),
        0,
    );
    const originalAmount = Math.abs(getInvoiceAmount(item));
    const latestStatus = latestActiveReturn
        ? getReturnStatus(latestActiveReturn).toLowerCase()
        : "";

    let status = "No Return";
    if (latestStatus === "draft") status = "Return Draft";
    else if (latestStatus === "approved") status = "Return Approved";
    else if (
        issuedAmount > 0 &&
        originalAmount > 0 &&
        issuedAmount >= originalAmount - 0.01
    ) {
        status = "Full Return";
    } else if (issuedAmount > 0) status = "Partial Return";
    else if (reservedAmount > 0) status = "Return Pending";
    else if (allReturns.length > 0) status = "Return Cancelled";

    return {
        status,
        issuedAmount,
        reservedAmount,
        count: allReturns.length,
        latestReturnInvoice,
    };
};

const getNetInvoiceAmount = (item) =>
    Math.max(getInvoiceAmount(item) - getReturnSummary(item).issuedAmount, 0);

// Ex-VAT counterpart of getNetInvoiceAmount, used for profit/margin only.
const getExVatNetInvoiceAmount = (item) =>
    Math.max(
        getExVatInvoiceAmount(item) - getReturnSummary(item).issuedAmount,
        0,
    );

const getBalanceAmount = (item) => {
    const hasPaidAmount = [
        item?.paidAmount,
        item?.paid_amount,
        item?.collectedAmount,
        item?.collected_amount,
    ].some((value) => value !== undefined && value !== null && value !== "");

    if (hasPaidAmount) {
        return Math.max(getNetInvoiceAmount(item) - getPaidAmount(item), 0);
    }

    const hasExplicitBalance = [item?.balanceAmount, item?.balance_amount].some(
        (value) => value !== undefined && value !== null && value !== "",
    );

    if (hasExplicitBalance) {
        return Math.max(
            firstDefinedNumber(item?.balanceAmount, item?.balance_amount) -
            getReturnSummary(item).issuedAmount,
            0,
        );
    }

    return Math.max(getNetInvoiceAmount(item), 0);
};

const getCustomerCreditAmount = (item) =>
    Math.max(getPaidAmount(item) - getNetInvoiceAmount(item), 0);

const getShipmentNo = (item) =>
    getValue(item, "jobNo", "job_no") ||
    item?.shipmentNo ||
    item?.shipment_no ||
    item?.invoiceNo ||
    item?.invoice_no ||
    "N/A";

const getCustomerName = (item) =>
    getValue(item, "customerName", "customer_name", item?.customer || "N/A");

const getSupplierName = (item) =>
    item?.supplierName ||
    item?.supplier_name ||
    item?.serviceProviderName ||
    item?.service_provider_name ||
    item?.linehaulServiceProvider ||
    item?.supplier?.companyName ||
    item?.serviceProvider?.companyName ||
    "N/A";

const getDateValue = (item) => {
    const raw =
        item?.invoiceDate ||
        item?.invoice_date ||
        item?.createdAt ||
        item?.created_at ||
        item?.updatedAt ||
        item?.updated_at;

    const date = raw ? new Date(raw) : null;
    return date && !Number.isNaN(date.getTime()) ? date : null;
};

const normalizeCountry = (value) =>
    String(value || "")
        .trim()
        .toLocaleUpperCase("tr-TR")
        .replace(/İ/g, "I")
        .replace(/Ü/g, "U");
const normalizeLookupKey = (value) => {
    if (!value) return "";
    if (typeof value === "object")
        return normalizeLookupKey(
            value?._id ||
            value?.id ||
            value?.$oid ||
            value?.nameKey ||
            value?.name ||
            value?.companyName,
        );
    return String(value)
        .trim()
        .toLocaleUpperCase("tr-TR")
        .normalize("NFKD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^A-Z0-9]+/g, " ")
        .trim();
};
const getDirectCountries = (item) =>
    [
        item,
        item?.customer,
        item?.serviceProvider,
        item?.supplier,
        item?.globalAgent,
        item?.selectedBookingAgent,
        item?.selectedOriginAgent,
        item?.selectedDestinationAgent,
        item?.selectedNotifyAgent,
    ]
        .filter((value) => value && typeof value === "object")
        .flatMap((value) => [
            value?.customerCountry,
            value?.serviceProviderCountry,
            value?.globalAgentCountry,
            ...(Array.isArray(value?.servicesCountry) ? value.servicesCountry : []),
        ])
        .filter(Boolean);
const getPartyKeys = (item) =>
    [
        item?.customerId,
        item?.customer,
        item?.customerName,
        item?.customer_name,
        item?.serviceProviderId,
        item?.serviceProvider,
        item?.serviceProviderName,
        item?.supplierId,
        item?.supplier,
        item?.supplierName,
        item?.supplier_name,
        item?.globalAgentId,
        item?.globalAgent,
        item?.globalAgentName,
        item?.selectedBookingAgent,
        item?.selectedOriginAgent,
        item?.selectedDestinationAgent,
        item?.selectedNotifyAgent,
    ]
        .map(normalizeLookupKey)
        .filter(Boolean);
const getGeographicScope = (item, lookup = new Map()) => {
    const countries = [
        ...getDirectCountries(item),
        ...getPartyKeys(item).flatMap((key) => lookup.get(key) || []),
    ];
    return countries.some((country) =>
        ["TURKEY", "TURKIYE"].includes(normalizeCountry(country)),
    )
        ? "DOMESTIC"
        : "INTERNATIONAL";
};
const getGeographicLabel = (item, lookup) =>
    getGeographicScope(item, lookup) === "DOMESTIC"
        ? "Domestic"
        : "International";

const getInvoiceNo = (item) =>
    getValue(item, "invoiceNo", "invoice_no") ||
    item?.edm?.invoiceId ||
    item?._id ||
    "N/A";

const getCollectionStatus = (item) => {
    const rawStatus = getValue(
        item,
        "collectionStatus",
        "collection_status",
        "Pending",
    );
    const netInvoiceAmount = getNetInvoiceAmount(item);
    const paidAmount = getPaidAmount(item);

    if (paidAmount > netInvoiceAmount + 0.01) return "Credit Due";
    if (netInvoiceAmount <= 0.01 && getReturnSummary(item).issuedAmount > 0) {
        return "Fully Returned";
    }
    if (netInvoiceAmount > 0 && paidAmount >= netInvoiceAmount - 0.01) {
        return "Collected";
    }
    if (paidAmount > 0) return "Partial";
    return rawStatus;
};

const RETURN_STATUS_STYLES = {
    "No Return": { color: "#475467", background: "#F2F4F7", border: "#D0D5DD" },
    "Return Draft": {
        color: "#B54708",
        background: "#FFFAEB",
        border: "#FEDF89",
    },
    "Return Approved": {
        color: "#175CD3",
        background: "#EFF8FF",
        border: "#B2DDFF",
    },
    "Partial Return": {
        color: "#6941C6",
        background: "#F4F3FF",
        border: "#D9D6FE",
    },
    "Full Return": { color: "#B42318", background: "#FEF3F2", border: "#FECDCA" },
    "Return Pending": {
        color: "#B54708",
        background: "#FFFAEB",
        border: "#FEDF89",
    },
    "Return Cancelled": {
        color: "#475467",
        background: "#F2F4F7",
        border: "#D0D5DD",
    },
};

const getDueDate = (item) => getValue(item, "dueDate", "due_date");

const getOverdueDays = (dueDate, collectionStatus) => {
    if (!dueDate) return 0;

    const status = String(collectionStatus || "").toLowerCase();

    if (
        status.includes("paid") ||
        status.includes("collected") ||
        status.includes("closed")
    ) {
        return 0;
    }

    const today = new Date();
    const due = new Date(dueDate);

    if (Number.isNaN(due.getTime())) return 0;

    // Whole calendar days past the due date: an invoice due today is not
    // overdue yet, and the time of day must not round a partial day up.
    const startOfDay = (value) =>
        new Date(value.getFullYear(), value.getMonth(), value.getDate());
    const days = Math.round(
        (startOfDay(today).getTime() - startOfDay(due).getTime()) / 86400000,
    );

    return days > 0 ? days : 0;
};

const getAgingBucket = (dueDate, collectionStatus) => {
    const overdueDays = getOverdueDays(dueDate, collectionStatus);

    if (overdueDays <= 0) return "Not Due";
    if (overdueDays <= 30) return "0-30";
    if (overdueDays <= 60) return "31-60";
    if (overdueDays <= 90) return "61-90";
    return "90+";
};

const getRiskScore = (item) => {
    const balance = getBalanceAmount(item);
    const overdueDays = getOverdueDays(
        getDueDate(item),
        getCollectionStatus(item),
    );

    if (balance <= 0 || overdueDays === 0) return "A";
    if (overdueDays <= 30) return "B";
    if (overdueDays <= 60) return "C";
    if (overdueDays <= 90) return "D";
    return "E";
};

const getRiskColor = (score) => {
    if (score === "A") return { bg: COLORS.greenLight, color: COLORS.green };
    if (score === "B") return { bg: COLORS.blueLight, color: COLORS.blue };
    if (score === "C") return { bg: COLORS.amberLight, color: COLORS.amber };

    return { bg: COLORS.redLight, color: COLORS.red };
};

const formatCompact = (value, currency = "EUR") =>
    new Intl.NumberFormat("en-US", {
        style: "currency",
        currency,
        notation: "compact",
        maximumFractionDigits: 1,
    }).format(Number(value || 0));

const formatNumber = (value) =>
    new Intl.NumberFormat("en-US", {
        maximumFractionDigits: 0,
    }).format(Number(value || 0));

// Options for the display-currency selector (which currency the aggregated
// KPIs / charts / CSV totals are shown in). NOTE: this is a different concept
// from `currencyFilter`, which decides WHICH RECORDS are listed based on each
// invoice's own currency.
const CURRENCY_OPTIONS = ["EUR", "USD", "TRY", "GBP"];

// Converts EVERY currency bucket into `targetCurrency` (even a single-entry
// bucket) and sums them. Used for numeric comparisons -- profit/margin ratios
// -- where revenue and cost must land in the SAME currency even when each side
// is individually single-currency (e.g. all revenue in EUR against all cost in
// TRY: neither bucket alone is "mixed", but subtracting them without
// converting one into the other is still wrong). Returns null when a rate is
// missing so callers can fall back instead of showing a wrong number.
const sumConvertedTo = (byCurrency, convert, targetCurrency = "EUR") => {
    const entries = Object.entries(byCurrency || {}).filter(
        ([, amount]) => amount !== 0,
    );
    if (entries.length === 0) return 0;
    if (typeof convert !== "function") return null;

    let total = 0;

    for (const [currency, amount] of entries) {
        const converted = convert(amount, currency, targetCurrency);
        if (converted === null || converted === undefined) return null;
        total += converted;
    }

    return total;
};

const getInvoiceOrigin = (item) =>
    String(item?.invoiceOrigin || item?.invoice_origin || "tms")
        .trim()
        .toLowerCase() === "manual"
        ? "manual"
        : "tms";

const getTransportMode = (item) => {
    if (getInvoiceOrigin(item) === "manual") {
        return "Manual";
    }

    const haystack = [
        item?.sourceType,
        item?.source_type,
        item?.transportMode,
        item?.transport_mode,
        item?.productType,
        item?.product_type,
        item?.serviceType,
        item?.service_type,
        item?.shipmentType,
        item?.shipment_type,
        item?.mode,
        getShipmentNo(item),
    ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

    if (
        haystack.includes("ocean") ||
        haystack.includes("sea") ||
        haystack.includes("fcl") ||
        haystack.includes("lcl") ||
        haystack.includes("ohfl")
    ) {
        return "Ocean";
    }

    if (
        haystack.includes("air") ||
        haystack.includes("airport") ||
        haystack.includes("ambl") ||
        haystack.includes("ahfl")
    ) {
        return "Air";
    }

    return "Road";
};

const getShipmentType = (item) => {
    const explicit =
        item?.shipmentType ||
        item?.shipment_type ||
        item?.serviceType ||
        item?.service_type ||
        item?.oceanTransportType ||
        item?.ocean_transport_type ||
        item?.roadTransportType ||
        item?.road_transport_type ||
        item?.airTransportType ||
        item?.air_transport_type;

    if (explicit) return String(explicit).toUpperCase();

    const text = [getTransportMode(item), getShipmentNo(item)]
        .join(" ")
        .toLowerCase();

    if (text.includes("lcl")) return "LCL";
    if (text.includes("fcl")) return "FCL";
    if (text.includes("ltl")) return "LTL";
    if (text.includes("ftl")) return "FTL";
    if (text.includes("air")) return "AIR";

    return getTransportMode(item).toUpperCase();
};

const TRANSPORT_META = {
    Ocean: {
        color: COLORS.blue,
        background: COLORS.blueLight,
        border: "#B2DDFF",
        icon: DirectionsBoat,
    },
    Road: {
        color: COLORS.amber,
        background: COLORS.amberLight,
        border: "#FEDF89",
        icon: LocalShipping,
    },
    Air: {
        color: COLORS.purple,
        background: COLORS.purpleLight,
        border: "#D9D6FE",
        icon: Flight,
    },
    Manual: {
        color: "#475467",
        background: "#F2F4F7",
        border: "#D0D5DD",
        icon: ReceiptLong,
    },
};

const ModeReceivableCard = ({ mode, data, currency = "EUR" }) => {
    const meta = TRANSPORT_META[mode] || TRANSPORT_META.Manual;
    const Icon = meta.icon;

    return (
        <Card
            sx={{
                ...cardSx,
                height: "100%",
                borderTop: `4px solid ${meta.color}`,
                transition: "transform .2s ease, box-shadow .2s ease",
                "&:hover": {
                    transform: "none",
                    boxShadow: "0 4px 14px rgba(15, 23, 42, 0.06)",
                },
            }}
        >
            <MDBox p={2.35}>
                <MDBox
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                >
                    <MDBox display="flex" alignItems="center" gap={1}>
                        <MDBox
                            width={42}
                            height={42}
                            borderRadius="8px"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            sx={{
                                color: meta.color,
                                background: meta.background,
                                border: `1px solid ${meta.border}`,
                            }}
                        >
                            <Icon sx={{ fontSize: "21px !important" }} />
                        </MDBox>

                        <MDBox>
                            <MDTypography
                                variant="h6"
                                fontWeight="bold"
                                sx={{ color: COLORS.text }}
                            >
                                {financeText(mode)}
                            </MDTypography>
                            <MDTypography variant="caption" sx={{ color: COLORS.subtext }}>
                                {data.count} {financeText("open receivables")}
                            </MDTypography>
                        </MDBox>
                    </MDBox>

                    <Chip
                        size="small"
                        label={`${data.collectionRate.toFixed(1)}% ${financeText("collected")}`}
                        sx={{
                            color: meta.color,
                            background: meta.background,
                            fontWeight: 800,
                        }}
                    />
                </MDBox>

                <Divider sx={{ my: 1.8 }} />

                <Grid container spacing={1.4}>
                    <Grid item xs={6}>
                        <MDTypography variant="caption" sx={{ color: COLORS.subtext }}>
                            {financeText("Outstanding")}
                        </MDTypography>
                        <MDTypography
                            variant="button"
                            fontWeight="bold"
                            display="block"
                            sx={{ color: COLORS.amber }}
                        >
                            {formatCompact(data.outstanding, currency)}
                        </MDTypography>
                    </Grid>
                    <Grid item xs={6}>
                        <MDTypography variant="caption" sx={{ color: COLORS.subtext }}>
                            {financeText("Overdue")}
                        </MDTypography>
                        <MDTypography
                            variant="button"
                            fontWeight="bold"
                            display="block"
                            sx={{ color: COLORS.red }}
                        >
                            {formatCompact(data.overdue, currency)}
                        </MDTypography>
                    </Grid>
                    <Grid item xs={6}>
                        <MDTypography variant="caption" sx={{ color: COLORS.subtext }}>
                            {financeText("Critical risk")}
                        </MDTypography>
                        <MDTypography
                            variant="button"
                            fontWeight="bold"
                            display="block"
                            sx={{ color: COLORS.purple }}
                        >
                            {data.criticalCount}
                        </MDTypography>
                    </Grid>
                    <Grid item xs={6}>
                        <MDTypography variant="caption" sx={{ color: COLORS.subtext }}>
                            {financeText("Gross profit")}
                        </MDTypography>
                        <MDTypography
                            variant="button"
                            fontWeight="bold"
                            display="block"
                            sx={{ color: data.profit >= 0 ? COLORS.green : COLORS.red }}
                        >
                            {formatCompact(data.profit, currency)}
                        </MDTypography>
                    </Grid>
                </Grid>
            </MDBox>
        </Card>
    );
};

const Receivables = () => {
    const navigate = useNavigate();

    const [financeLanguage, setFinanceLanguageState] = useState(getFinanceLanguage);

    const changeFinanceLanguage = (event) => {
        const nextLanguage = event.target.value === "tr" ? "tr" : "en";
        saveFinanceLanguage(nextLanguage);
        setFinanceLanguageState(nextLanguage);
    };

    const [receivables, setReceivables] = useState([]);
    const [businessPartners, setBusinessPartners] = useState([]);
    const [loading, setLoading] = useState(true);
    const [lastUpdated, setLastUpdated] = useState(new Date());

    const [search, setSearch] = useState("");
    const [collectionStatus, setCollectionStatus] = useState("all");
    const [returnStatus, setReturnStatus] = useState("all");
    const [agingBucket, setAgingBucket] = useState("all");
    const [riskScore, setRiskScore] = useState("all");
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const todayValue = `${currentYear}-${String(currentDate.getMonth() + 1).padStart(2, "0")}-${String(currentDate.getDate()).padStart(2, "0")}`;
    const [startDate, setStartDate] = useState(`${currentYear}-01-01`);
    const [endDate, setEndDate] = useState(todayValue);
    const [selectedParty, setSelectedParty] = useState(null);
    const [transportMode, setTransportMode] = useState("all");
    const [invoiceOrigin, setInvoiceOrigin] = useState("all");
    const [currencyFilter, setCurrencyFilter] = useState("all");
    const [geographicScope, setGeographicScope] = useState("all");
    const [exchangeRates, setExchangeRates] = useState(null);
    // Display currency: which currency the aggregated KPIs / charts / CSV
    // totals are converted into before being shown. Deliberately separate from
    // `currencyFilter` above, which only decides WHICH RECORDS are listed.
    const [selectedCurrency, setSelectedCurrency] = useState("EUR");

    const [noteModalOpen, setNoteModalOpen] = useState(false);
    const [selectedReceivable, setSelectedReceivable] = useState(null);

    const [noteForm, setNoteForm] = useState({
        promiseToPayDate: "",
        collectionNote: "",
        nextFollowUpDate: "",
    });

    const token = localStorage.getItem("token");
    // Every aggregated figure below (KPIs, hero stat, mode/type charts, aging
    // buckets, CSV export) is converted into this currency before display.
    // It follows the user's explicit display-currency selection -- it is NOT
    // derived from `currencyFilter` any more, because narrowing the visible
    // records to one currency and choosing what currency to report totals in
    // are two independent decisions.
    const reportingCurrency = normalizeCurrencyCode(selectedCurrency);

    const fetchWithAuth = useCallback(
        async (path) => {
            const response = await fetch(`${API_URL}${path}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                throw new Error(`${path} fetch failed`);
            }

            const data = await response.json();
            const candidates = [
                data,
                data?.data,
                data?.receivables,
                data?.invoices,
                data?.returnInvoices,
                data?.return_invoices,
                data?.jobs,
                data?.results,
                data?.records,
                data?.data?.data,
                data?.data?.receivables,
                data?.data?.invoices,
                data?.data?.returnInvoices,
                data?.data?.return_invoices,
                data?.data?.jobs,
                data?.data?.results,
                data?.data?.records,
            ];

            return candidates.find(Array.isArray) || [];
        },
        [token],
    );

    useEffect(() => {
        const loadExchangeRates = async () => {
            try {
                const response = await fetch(
                    "https://api.exchangerate-api.com/v4/latest/USD",
                );
                if (!response.ok) throw new Error("Exchange rates could not be loaded");
                const data = await response.json();
                setExchangeRates(data?.rates || null);
            } catch (error) {
                console.error("Exchange rate fetch error:", error);
                setExchangeRates(null);
            }
        };

        loadExchangeRates();
    }, []);

    const convertCurrency = useCallback(
        (amount, fromCurrency, toCurrency) => {
            const value = Number(amount) || 0;
            const from = normalizeCurrencyCode(fromCurrency);
            const to = normalizeCurrencyCode(toCurrency);

            if (from === to) return value;
            if (!exchangeRates?.[from] || !exchangeRates?.[to]) return null;

            return (value / exchangeRates[from]) * exchangeRates[to];
        },
        [exchangeRates],
    );

    const getConvertedFinancials = useCallback(
        (item, targetCurrency = reportingCurrency) => {
            const invoice = convertCurrency(
                getExVatNetInvoiceAmount(item),
                getCurrency(item),
                targetCurrency,
            );
            const cost = convertCurrency(
                getTotalCost(item),
                getCostCurrency(item),
                targetCurrency,
            );

            if (invoice === null || cost === null) {
                return { invoice: null, cost: null, profit: null, margin: null };
            }

            const profit = invoice - cost;
            return {
                invoice,
                cost,
                profit,
                margin: invoice > 0 ? (profit / invoice) * 100 : 0,
            };
        },
        [convertCurrency, reportingCurrency],
    );

    const fetchReceivables = useCallback(async () => {
        try {
            setLoading(true);

            const [receivableData, manualInvoices, returnInvoices] =
                await Promise.all([
                    fetchWithAuth("/finance/receivables"),
                    fetchWithAuth(
                        "/finance/manual-invoices?invoiceType=sales&limit=200",
                    ).catch((manualInvoiceError) => {
                        console.warn(
                            "Manual invoices could not be loaded:",
                            manualInvoiceError,
                        );
                        return [];
                    }),
                    fetchWithAuth("/finance/return-invoices?invoiceType=sales").catch(
                        (returnInvoiceError) => {
                            console.warn(
                                "Return invoices could not be loaded:",
                                returnInvoiceError,
                            );
                            return [];
                        },
                    ),
                ]);
            const uniqueRecords = new Map();

            [...manualInvoices, ...receivableData]
                .filter(
                    (record) =>
                        (!record?.invoiceType ||
                            String(record.invoiceType).toLowerCase() === "sales") &&
                        getBalanceAmount(record) > 0,
                )
                .forEach((record, index) => {
                    const key =
                        record?._id ||
                        record?.id ||
                        record?.invoiceNo ||
                        record?.invoice_no ||
                        `receivable-${index}`;
                    uniqueRecords.set(String(key), record);
                });

            const uniqueReturnInvoices = Array.from(
                new Map(
                    returnInvoices.map((returnInvoice, index) => [
                        getEntityId(returnInvoice?._id || returnInvoice?.id) ||
                        `return-${index}`,
                        returnInvoice,
                    ]),
                ).values(),
            );
            const returnsByOriginalId = new Map();
            const returnsByOriginalNo = new Map();

            uniqueReturnInvoices.forEach((returnInvoice) => {
                const originalInvoiceId = getOriginalInvoiceId(returnInvoice);
                const originalInvoiceNo = normalizeLookupKey(
                    getOriginalInvoiceNo(returnInvoice),
                );

                if (originalInvoiceId) {
                    returnsByOriginalId.set(originalInvoiceId, [
                        ...(returnsByOriginalId.get(originalInvoiceId) || []),
                        returnInvoice,
                    ]);
                }
                if (originalInvoiceNo) {
                    returnsByOriginalNo.set(originalInvoiceNo, [
                        ...(returnsByOriginalNo.get(originalInvoiceNo) || []),
                        returnInvoice,
                    ]);
                }
            });

            const enrichedReceivables = Array.from(uniqueRecords.values()).map(
                (record) => {
                    const identityKeys = [
                        record?._id,
                        record?.id,
                        record?.invoiceId,
                        record?.invoice_id,
                        record?.customerInvoiceId,
                        record?.customer_invoice_id,
                    ]
                        .map(getEntityId)
                        .filter(Boolean);
                    const invoiceNoKey = normalizeLookupKey(getInvoiceNo(record));
                    const linkedReturns = [
                        ...getLinkedReturnInvoices(record),
                        ...identityKeys.flatMap(
                            (identityKey) => returnsByOriginalId.get(identityKey) || [],
                        ),
                        ...(returnsByOriginalNo.get(invoiceNoKey) || []),
                    ];
                    const uniqueLinkedReturns = Array.from(
                        new Map(
                            linkedReturns.map((returnInvoice, index) => [
                                getEntityId(returnInvoice?._id || returnInvoice?.id) ||
                                `${invoiceNoKey}-return-${index}`,
                                returnInvoice,
                            ]),
                        ).values(),
                    );

                    return {
                        ...record,
                        returnInvoices: uniqueLinkedReturns,
                    };
                },
            );

            setReceivables(enrichedReceivables);
            setLastUpdated(new Date());
        } catch (error) {
            console.error("Receivables fetch error:", error);
            setReceivables([]);
        } finally {
            setLoading(false);
        }
    }, [fetchWithAuth]);

    const fetchBusinessPartners = useCallback(async () => {
        const firstAvailable = async (paths) => {
            for (const path of paths) {
                try {
                    return await fetchWithAuth(path);
                } catch (error) {
                    console.warn(`${path} lookup failed`, error);
                }
            }
            return [];
        };
        const [customers, providers, agents] = await Promise.all([
            firstAvailable(["/customer", "/customers"]),
            firstAvailable(["/serviceProvider", "/service-provider"]),
            firstAvailable(["/globalAgent", "/global-agent"]),
        ]);
        setBusinessPartners([...customers, ...providers, ...agents]);
    }, [fetchWithAuth]);

    useEffect(() => {
        fetchReceivables();
        fetchBusinessPartners();
    }, [fetchReceivables, fetchBusinessPartners]);

    const partyCountryLookup = useMemo(() => {
        const lookup = new Map();
        businessPartners.forEach((record) => {
            const item = record?.attributes || record;
            const countries = [
                item?.customerCountry,
                item?.serviceProviderCountry,
                item?.globalAgentCountry,
                ...(Array.isArray(item?.servicesCountry) ? item.servicesCountry : []),
            ].filter(Boolean);
            [
                record?.id,
                item?._id,
                item?.id,
                item?.name,
                item?.nameKey,
                item?.companyName,
                item?.customerNo,
                item?.serviceProviderNo,
                item?.globalAgentNo,
            ]
                .map(normalizeLookupKey)
                .filter(Boolean)
                .forEach((key) =>
                    lookup.set(key, [...(lookup.get(key) || []), ...countries]),
                );
        });
        return lookup;
    }, [businessPartners]);

    const statusOptions = useMemo(() => {
        const values = receivables
            .map((item) => String(getCollectionStatus(item)).trim())
            .filter(Boolean);

        return ["all", ...Array.from(new Set(values))];
    }, [receivables]);

    const partyOptions = useMemo(() => {
        const unique = new Map();

        receivables.forEach((item) => {
            const customerName = getCustomerName(item);
            const supplierName = getSupplierName(item);

            if (customerName && customerName !== "N/A") {
                unique.set(`CUSTOMER-${customerName}`, {
                    type: "CUSTOMER",
                    name: customerName,
                    label: `${financeText("Customer")} - ${customerName}`,
                });
            }

            if (supplierName && supplierName !== "N/A") {
                unique.set(`SUPPLIER-${supplierName}`, {
                    type: "SUPPLIER",
                    name: supplierName,
                    label: `${financeText("Supplier")} - ${supplierName}`,
                });
            }
        });

        return Array.from(unique.values()).sort((a, b) =>
            a.label.localeCompare(b.label),
        );
    }, [receivables]);

    const transportModeOptions = useMemo(
        () => Array.from(new Set(receivables.map(getTransportMode))).sort(),
        [receivables],
    );

    const currencyOptions = useMemo(
        () =>
            Array.from(
                new Set(
                    receivables.map((item) => String(getCurrency(item)).toUpperCase()),
                ),
            ).sort(),
        [receivables],
    );

    const returnStatusOptions = useMemo(
        () => [
            "all",
            ...Array.from(
                new Set(receivables.map((item) => getReturnSummary(item).status)),
            ),
        ],
        [receivables],
    );

    const filteredReceivables = useMemo(() => {
        const searchText = search.toLowerCase().trim();
        const rangeStart = startDate ? new Date(`${startDate}T00:00:00`) : null;
        const rangeEnd = endDate ? new Date(`${endDate}T23:59:59`) : null;

        return receivables.filter((item) => {
            const jobNo = String(getShipmentNo(item)).toLowerCase();
            const customer = String(getCustomerName(item)).toLowerCase();
            const supplier = String(getSupplierName(item)).toLowerCase();
            const invoiceDate = getDateValue(item);
            const invoiceNo = String(getInvoiceNo(item)).toLowerCase();
            const email = String(item?.email || "").toLowerCase();
            const currentStatus = String(getCollectionStatus(item)).toLowerCase();
            const currentReturnStatus = getReturnSummary(item).status.toLowerCase();
            const returnDocumentText = getLinkedReturnInvoices(item)
                .map(getReturnInvoiceNo)
                .join(" ")
                .toLowerCase();

            const currentBucket = getAgingBucket(
                getDueDate(item),
                getCollectionStatus(item),
            );

            const currentRisk = getRiskScore(item);
            const balance = getBalanceAmount(item);

            const matchesSearch =
                !searchText ||
                jobNo.includes(searchText) ||
                customer.includes(searchText) ||
                supplier.includes(searchText) ||
                invoiceNo.includes(searchText) ||
                email.includes(searchText) ||
                returnDocumentText.includes(searchText);

            const matchesStartDate =
                !rangeStart || (invoiceDate && invoiceDate >= rangeStart);
            const matchesEndDate =
                !rangeEnd || (invoiceDate && invoiceDate <= rangeEnd);
            const matchesParty =
                !selectedParty ||
                (selectedParty.type === "CUSTOMER"
                    ? getCustomerName(item) === selectedParty.name
                    : getSupplierName(item) === selectedParty.name);
            const matchesTransportMode =
                transportMode === "all" || getTransportMode(item) === transportMode;
            const matchesInvoiceOrigin =
                invoiceOrigin === "all" || getInvoiceOrigin(item) === invoiceOrigin;
            const matchesCurrency =
                currencyFilter === "all" ||
                String(getCurrency(item)).toUpperCase() === currencyFilter;
            const matchesGeographicScope =
                geographicScope === "all" ||
                getGeographicScope(item, partyCountryLookup) === geographicScope;

            const matchesStatus =
                collectionStatus === "all" ||
                currentStatus === collectionStatus.toLowerCase();
            const matchesReturnStatus =
                returnStatus === "all" ||
                currentReturnStatus === returnStatus.toLowerCase();

            const matchesBucket =
                agingBucket === "all" ||
                currentBucket.toLowerCase() === agingBucket.toLowerCase();

            const matchesRisk = riskScore === "all" || currentRisk === riskScore;
            const matchesOpenBalance = balance > 0 || returnStatus !== "all";

            return (
                matchesSearch &&
                matchesStartDate &&
                matchesEndDate &&
                matchesParty &&
                matchesTransportMode &&
                matchesInvoiceOrigin &&
                matchesCurrency &&
                matchesGeographicScope &&
                matchesStatus &&
                matchesReturnStatus &&
                matchesBucket &&
                matchesRisk &&
                matchesOpenBalance
            );
        });
    }, [
        receivables,
        search,
        collectionStatus,
        returnStatus,
        agingBucket,
        riskScore,
        startDate,
        endDate,
        selectedParty,
        transportMode,
        invoiceOrigin,
        currencyFilter,
        geographicScope,
        partyCountryLookup,
    ]);

    const stats = useMemo(() => {
        const countedCostJobs = new Set();
        const countedOperationalCostJobs = new Set();
        const countedBuyingRateJobs = new Set();
        const totalInvoice = filteredReceivables.reduce(
            (sum, item) => sum + (getConvertedFinancials(item).invoice ?? 0),
            0,
        );

        // Billed (VAT-inclusive where applicable) counterpart of totalInvoice;
        // collections are measured against what customers actually owe.
        const billedInvoice = filteredReceivables.reduce(
            (sum, item) =>
                sum +
                (convertCurrency(
                    getNetInvoiceAmount(item),
                    getCurrency(item),
                    reportingCurrency,
                ) ?? 0),
            0,
        );

        const paid = filteredReceivables.reduce(
            (sum, item) =>
                sum +
                (convertCurrency(
                    getPaidAmount(item),
                    getCurrency(item),
                    reportingCurrency,
                ) ?? 0),
            0,
        );

        const returnedAmount = filteredReceivables.reduce(
            (sum, item) =>
                sum +
                (convertCurrency(
                    getReturnSummary(item).issuedAmount,
                    getCurrency(item),
                    reportingCurrency,
                ) ?? 0),
            0,
        );

        const customerCreditAmount = filteredReceivables.reduce(
            (sum, item) =>
                sum +
                (convertCurrency(
                    getCustomerCreditAmount(item),
                    getCurrency(item),
                    reportingCurrency,
                ) ?? 0),
            0,
        );

        const outstanding = filteredReceivables.reduce(
            (sum, item) =>
                sum +
                (convertCurrency(
                    getBalanceAmount(item),
                    getCurrency(item),
                    reportingCurrency,
                ) ?? 0),
            0,
        );

        const overdueAmount = filteredReceivables
            .filter(
                (item) =>
                    getOverdueDays(getDueDate(item), getCollectionStatus(item)) > 0,
            )
            .reduce(
                (sum, item) =>
                    sum +
                    (convertCurrency(
                        getBalanceAmount(item),
                        getCurrency(item),
                        reportingCurrency,
                    ) ?? 0),
                0,
            );

        const buckets = filteredReceivables.reduce(
            (accumulator, item) => {
                const bucket = getAgingBucket(
                    getDueDate(item),
                    getCollectionStatus(item),
                );

                accumulator[bucket] +=
                    convertCurrency(
                        getBalanceAmount(item),
                        getCurrency(item),
                        reportingCurrency,
                    ) ?? 0;
                return accumulator;
            },
            {
                "Not Due": 0,
                "0-30": 0,
                "31-60": 0,
                "61-90": 0,
                "90+": 0,
            },
        );

        const weightedOverdueDays = filteredReceivables.reduce((sum, item) => {
            const balance =
                convertCurrency(
                    getBalanceAmount(item),
                    getCurrency(item),
                    reportingCurrency,
                ) ?? 0;
            const days = getOverdueDays(getDueDate(item), getCollectionStatus(item));

            return sum + balance * days;
        }, 0);

        const dso = outstanding ? weightedOverdueDays / outstanding : 0;

        const collectionRate = billedInvoice ? (paid / billedInvoice) * 100 : 0;

        const criticalCount = filteredReceivables.filter((item) =>
            ["D", "E"].includes(getRiskScore(item)),
        ).length;

        const totalCost = filteredReceivables.reduce((sum, item, index) => {
            const jobKey = getFinanceJobKey(item, index);
            if (countedCostJobs.has(jobKey)) return sum;
            countedCostJobs.add(jobKey);
            return sum + (getConvertedFinancials(item).cost ?? 0);
        }, 0);

        const operationalCost = filteredReceivables.reduce((sum, item, index) => {
            const jobKey = getFinanceJobKey(item, index);
            if (countedOperationalCostJobs.has(jobKey)) return sum;
            countedOperationalCostJobs.add(jobKey);
            return (
                sum +
                (convertCurrency(
                    getOperationalCost(item),
                    getCostCurrency(item),
                    reportingCurrency,
                ) ?? 0)
            );
        }, 0);

        const buyingRate = filteredReceivables.reduce((sum, item, index) => {
            const jobKey = getFinanceJobKey(item, index);
            if (countedBuyingRateJobs.has(jobKey)) return sum;
            countedBuyingRateJobs.add(jobKey);
            return (
                sum +
                (convertCurrency(
                    getBuyingRate(item),
                    getCostCurrency(item),
                    reportingCurrency,
                ) ?? 0)
            );
        }, 0);

        const profit = totalInvoice - totalCost;
        const margin = totalInvoice ? (profit / totalInvoice) * 100 : 0;

        return {
            count: filteredReceivables.length,
            totalInvoice,
            paid,
            returnedAmount,
            customerCreditAmount,
            outstanding,
            overdueAmount,
            buckets,
            dso,
            collectionRate,
            criticalCount,
            totalCost,
            operationalCost,
            buyingRate,
            profit,
            margin,
        };
    }, [
        filteredReceivables,
        getConvertedFinancials,
        convertCurrency,
        reportingCurrency,
    ]);

    const transportAnalytics = useMemo(() => {
        const base = {
            Ocean: {
                count: 0,
                invoice: 0,
                paid: 0,
                outstanding: 0,
                overdue: 0,
                criticalCount: 0,
                cost: 0,
                profit: 0,
                collectionRate: 0,
            },
            Road: {
                count: 0,
                invoice: 0,
                paid: 0,
                outstanding: 0,
                overdue: 0,
                criticalCount: 0,
                cost: 0,
                profit: 0,
                collectionRate: 0,
            },
            Air: {
                count: 0,
                invoice: 0,
                paid: 0,
                outstanding: 0,
                overdue: 0,
                criticalCount: 0,
                cost: 0,
                profit: 0,
                collectionRate: 0,
            },
            Manual: {
                count: 0,
                invoice: 0,
                paid: 0,
                outstanding: 0,
                overdue: 0,
                criticalCount: 0,
                cost: 0,
                profit: 0,
                collectionRate: 0,
            },
        };

        const countedCostJobs = new Set();

        filteredReceivables.forEach((item, index) => {
            const mode = getTransportMode(item);
            const current = base[mode];
            const financials = getConvertedFinancials(item);
            const invoice = financials.invoice ?? 0;
            const jobKey = getFinanceJobKey(item, index);
            const shouldCountCost = !countedCostJobs.has(jobKey);
            const cost = shouldCountCost ? (financials.cost ?? 0) : 0;
            if (shouldCountCost) countedCostJobs.add(jobKey);
            const paid =
                convertCurrency(
                    getPaidAmount(item),
                    getCurrency(item),
                    reportingCurrency,
                ) ?? 0;
            const outstanding =
                convertCurrency(
                    getBalanceAmount(item),
                    getCurrency(item),
                    reportingCurrency,
                ) ?? 0;
            const overdueDays = getOverdueDays(
                getDueDate(item),
                getCollectionStatus(item),
            );

            current.count += 1;
            current.invoice += invoice;
            current.paid += paid;
            current.outstanding += outstanding;
            current.cost += cost;

            if (overdueDays > 0) current.overdue += outstanding;
            if (["D", "E"].includes(getRiskScore(item))) current.criticalCount += 1;
        });

        Object.values(base).forEach((item) => {
            item.collectionRate = item.invoice ? (item.paid / item.invoice) * 100 : 0;
            item.profit = item.invoice - item.cost;
        });

        return base;
    }, [
        filteredReceivables,
        getConvertedFinancials,
        convertCurrency,
        reportingCurrency,
    ]);

    const shipmentTypeAnalytics = useMemo(() => {
        // Balances are grouped by each record's OWN currency first, then
        // converted into `reportingCurrency` in one pass. Previously these
        // sums added raw amounts straight across currencies (a EUR balance and
        // a TRY balance summed as if they were the same unit) and the chart
        // then rendered the result with formatCompact's default "€" label --
        // a wrong number under a wrong symbol.
        const result = {};

        filteredReceivables.forEach((item) => {
            const type = getShipmentType(item);
            const currency = normalizeCurrencyCode(getCurrency(item));
            const balance = getBalanceAmount(item);

            if (!result[type]) {
                result[type] = {
                    count: 0,
                    outstandingByCurrency: {},
                    overdueByCurrency: {},
                };
            }

            result[type].count += 1;
            result[type].outstandingByCurrency[currency] =
                (result[type].outstandingByCurrency[currency] || 0) + balance;

            if (getOverdueDays(getDueDate(item), getCollectionStatus(item)) > 0) {
                result[type].overdueByCurrency[currency] =
                    (result[type].overdueByCurrency[currency] || 0) + balance;
            }
        });

        return Object.entries(result)
            .map(([type, values]) => ({
                type,
                count: values.count,
                outstanding:
                    sumConvertedTo(
                        values.outstandingByCurrency,
                        convertCurrency,
                        reportingCurrency,
                    ) ?? 0,
                overdue:
                    sumConvertedTo(
                        values.overdueByCurrency,
                        convertCurrency,
                        reportingCurrency,
                    ) ?? 0,
            }))
            .sort((a, b) => b.outstanding - a.outstanding);
    }, [filteredReceivables, convertCurrency, reportingCurrency]);

    const openLatestReturnInvoice = (item) => {
        const latestReturnInvoice = getReturnSummary(item).latestReturnInvoice;
        const returnInvoiceId = getEntityId(
            latestReturnInvoice?._id || latestReturnInvoice?.id,
        );

        if (!returnInvoiceId) {
            appAlert(financeText("No linked return invoice was found."));
            return;
        }

        navigate(`/invoice/finance/return-invoices/${returnInvoiceId}`, {
            state: {
                returnInvoice: latestReturnInvoice,
                originalInvoice: item,
                originalInvoiceId: getOriginalInvoiceId(latestReturnInvoice),
            },
        });
    };

    const openInvoiceDetails = (item) => {
        const invoiceId =
            item?._id || item?.id || item?.invoiceId || item?.invoice_id;

        if (getInvoiceOrigin(item) === "manual") {
            if (!invoiceId) {
                appAlert(financeText("Manual Invoice ID not found!"));
                return;
            }

            navigate(`/invoice/finance/jobs/${invoiceId}`, {
                state: {
                    job: item,
                    financeJobId: invoiceId,
                    invoiceOrigin: "manual",
                },
            });
            return;
        }

        const financeJobId =
            item?.financeJobId ||
            item?.finance_job_id ||
            item?.jobFinanceId ||
            item?.job_finance_id ||
            invoiceId;

        if (!financeJobId) {
            appAlert(financeText("Finance Job ID not found!"));
            return;
        }

        navigate(`/invoice/finance/jobs/${financeJobId}`, {
            state: {
                job: item,
                financeJobId,
            },
        });
    };

    const openInvoiceEditor = async (item) => {
        const invoiceId =
            item?._id || item?.id || item?.invoiceId || item?.invoice_id;

        if (getInvoiceOrigin(item) === "manual") {
            if (!invoiceId) {
                appAlert(financeText("Manual Invoice ID not found!"));
                return;
            }

            navigate(`/invoice/finance/manual-invoices/${invoiceId}/edit`, {
                state: {
                    invoice: item,
                    invoiceId,
                    editMode: true,
                },
            });
            return;
        }

        let resolvedItem = item;

        if (invoiceId) {
            const detailPaths = [
                `/finance/manual-invoices/${invoiceId}`,
                `/invoice/${invoiceId}`,
                `/finance/customer-invoices/${invoiceId}`,
                `/finance/jobs/${invoiceId}`,
            ];

            for (const path of detailPaths) {
                try {
                    const response = await fetch(`${API_URL}${path}`, {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    });

                    if (!response.ok) continue;

                    const payload = await response.json();
                    const detail =
                        payload?.data?.invoice ||
                        payload?.data?.job ||
                        payload?.data?.data ||
                        payload?.invoice ||
                        payload?.job ||
                        payload?.data ||
                        payload;

                    if (detail && typeof detail === "object") {
                        resolvedItem = {
                            ...item,
                            ...detail,
                            invoiceItems:
                                detail?.invoiceItems ||
                                detail?.invoice_items ||
                                item?.invoiceItems ||
                                item?.invoice_items ||
                                [],
                        };
                        break;
                    }
                } catch (error) {
                    console.warn(`${path} invoice detail lookup failed`, error);
                }
            }
        }

        const shipmentId =
            resolvedItem?.shipmentId ||
            resolvedItem?.shipment_id ||
            resolvedItem?._id ||
            resolvedItem?.id;

        if (!shipmentId) {
            appAlert(financeText("Shipment ID not found!"));
            return;
        }

        const sellingCurrency = getCurrency(resolvedItem);
        const costCurrency = getCostCurrency(resolvedItem);
        const sourceItems =
            [
                resolvedItem?.invoiceItems,
                resolvedItem?.invoice_items,
                resolvedItem?.cargoDetails,
                resolvedItem?.cargo_details,
            ].find(Array.isArray) || [];
        const normalizedItems = sourceItems.map((invoiceItem) => {
            const rawItem = invoiceItem?.attributes || invoiceItem;
            const unitQuantity =
                firstDefinedNumber(
                    rawItem?.unitQuantity,
                    rawItem?.unit_quantity,
                    rawItem?.quantity,
                    rawItem?.qty,
                ) || 1;
            const explicitUnitPrice = firstDefinedNumber(
                rawItem?.unitPrice,
                rawItem?.unit_price,
                rawItem?.salesPrice,
                rawItem?.price,
            );
            const explicitSellingRate = firstDefinedNumber(
                rawItem?.sellingRate,
                rawItem?.selling_rate,
                rawItem?.salesAmount,
            );
            const amountBasedUnitPrice =
                firstDefinedNumber(rawItem?.amount) / unitQuantity;
            const unitPrice =
                explicitUnitPrice > 0
                    ? explicitUnitPrice
                    : explicitSellingRate > 0
                        ? explicitSellingRate
                        : amountBasedUnitPrice;

            return {
                ...rawItem,
                commodity:
                    rawItem?.commodity ||
                    rawItem?.invoiceItem ||
                    rawItem?.invoice_item ||
                    rawItem?.label ||
                    "",
                description: rawItem?.description || "",
                totalCost: firstDefinedNumber(
                    rawItem?.totalCost,
                    rawItem?.total_cost,
                    rawItem?.cost,
                    rawItem?.unitCost,
                    rawItem?.unit_cost,
                ),
                costCurrency: normalizeCurrencyCode(
                    rawItem?.costCurrency || rawItem?.cost_currency || costCurrency,
                ),
                unitPrice,
                sellingRate: unitPrice,
                sellingRateCurrency: normalizeCurrencyCode(
                    rawItem?.sellingRateCurrency ||
                    rawItem?.selling_rate_currency ||
                    rawItem?.currency ||
                    sellingCurrency,
                ),
                unitQuantity,
                amount: unitPrice * unitQuantity,
            };
        });
        const invoiceItems = normalizedItems.length
            ? normalizedItems
            : [
                {
                    commodity: "Freight Charge",
                    description: "Shipment freight service",
                    totalCost: getTotalCost(resolvedItem),
                    costCurrency,
                    unitPrice: getExVatInvoiceAmount(resolvedItem),
                    sellingRate: getExVatInvoiceAmount(resolvedItem),
                    sellingRateCurrency: sellingCurrency,
                    unitQuantity: 1,
                    amount: getExVatInvoiceAmount(resolvedItem),
                },
            ];
        const financials = getConvertedFinancials(resolvedItem, sellingCurrency);
        const detectedMode = String(getTransportMode(resolvedItem)).toLowerCase();
        const sourceType = ["road", "ocean", "air"].includes(detectedMode)
            ? detectedMode
            : "";

        navigate("/create-invoice", {
            state: {
                openInvoiceModal: true,
                editMode: true,
                // ViewShipments derives its own isEditMode (PUT vs POST) from
                // this field — without it, this silently created a
                // brand-new duplicate invoice instead of updating the one
                // being edited.
                invoiceId,
                shipments: [
                    {
                        ...resolvedItem,
                        _id: shipmentId,
                        shipmentNo: String(
                            resolvedItem?.shipmentNo || resolvedItem?.jobNo || "",
                        ).replace(/^(OHFL|RHFL|AHFL)/, ""),
                        customerName: getCustomerName(resolvedItem),
                        customer: getCustomerName(resolvedItem),
                        email: resolvedItem?.email || "",
                        invoiceItems,
                        cargoDetails: invoiceItems,
                        sellingRateCurrency: sellingCurrency,
                        costCurrency,
                        totalCostCurrency: costCurrency,
                        invoiceSalesAmount: getExVatInvoiceAmount(resolvedItem),
                        salesAmount: getExVatInvoiceAmount(resolvedItem),
                        sellingRate: getExVatInvoiceAmount(resolvedItem),
                        totalSellingRate: getExVatInvoiceAmount(resolvedItem),
                        totalAmount: getExVatInvoiceAmount(resolvedItem),
                        customerInvoiceAmount: getExVatInvoiceAmount(resolvedItem),
                        actualRevenueTotal: getExVatInvoiceAmount(resolvedItem),
                        expectedRevenueTotal: getExVatInvoiceAmount(resolvedItem),

                        operationalCost: getOperationalCost(resolvedItem),
                        buyingRate: getBuyingRate(resolvedItem),
                        actualCostTotal: getTotalCost(resolvedItem),
                        expectedCostTotal: getTotalCost(resolvedItem),
                        totalCost: getTotalCost(resolvedItem),
                        totalCostIncludesBuyingRate: true,

                        actualProfit: financials.profit ?? 0,
                        expectedProfit: financials.profit ?? 0,
                        actualMargin: financials.margin ?? 0,

                        sourceType,
                        currency: sellingCurrency,
                    },
                ],
            },
        });
    };

    const addCollection = (item) => {
        navigate("/invoice/finance/collections", {
            state: {
                invoice: item,
                openCollectionModal: true,
                financeJobId:
                    item?.jobFinanceId ||
                    item?.job_finance_id ||
                    item?.financeJobId ||
                    item?.finance_job_id,
                shipmentId: item?.shipmentId || item?.shipment_id,
                jobNo: getShipmentNo(item),
                shipmentNo: getShipmentNo(item),
                operationalCost: getOperationalCost(item),
                buyingRate: getBuyingRate(item),
                actualCostTotal: getTotalCost(item),
                actualProfit: getProfit(item),
                actualMargin: getMargin(item),
            },
        });
    };

    const sendReminder = (item) => {
        if (!item?.email) {
            appAlert(financeText("Customer email not found."));
            return;
        }

        const subject = encodeURIComponent(
            `Payment Reminder - Invoice ${getInvoiceNo(item)}`,
        );

        const body = encodeURIComponent(
            `Dear ${getCustomerName(item)},\n\n` +
            `This is a kind reminder for the outstanding balance of invoice ${getInvoiceNo(
                item,
            )}.\n\n` +
            `Outstanding Balance: ${formatCurrency(
                getBalanceAmount(item),
                getCurrency(item),
            )}\n` +
            `Due Date: ${formatDate(getDueDate(item))}\n\n` +
            "Best regards.",
        );

        window.location.href = `mailto:${item.email}?subject=${subject}&body=${body}`;
    };

    const openNoteModal = (item) => {
        setSelectedReceivable(item);

        setNoteForm({
            promiseToPayDate: item?.promiseToPayDate
                ? String(item.promiseToPayDate).slice(0, 10)
                : "",
            collectionNote: item?.collectionNote || "",
            nextFollowUpDate: item?.nextFollowUpDate
                ? String(item.nextFollowUpDate).slice(0, 10)
                : "",
        });

        setNoteModalOpen(true);
    };

    const saveNote = async () => {
        const id = selectedReceivable?._id || selectedReceivable?.id;

        if (!id) {
            appAlert(financeText("Invoice ID not found."));
            return;
        }

        try {
            const response = await fetch(`${API_URL}/invoice/${id}`, {
                method: "PATCH",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    promiseToPayDate: noteForm.promiseToPayDate || null,
                    collectionNote: noteForm.collectionNote,
                    nextFollowUpDate: noteForm.nextFollowUpDate || null,
                }),
            });

            if (!response.ok) {
                throw new Error("Receivable note update failed");
            }

            setReceivables((previous) =>
                previous.map((item) =>
                    (item?._id || item?.id) === id
                        ? {
                            ...item,
                            promiseToPayDate: noteForm.promiseToPayDate,
                            collectionNote: noteForm.collectionNote,
                            nextFollowUpDate: noteForm.nextFollowUpDate,
                        }
                        : item,
                ),
            );

            setNoteModalOpen(false);
        } catch (error) {
            console.error("Receivable note save error:", error);

            appAlert(
                financeText(
                    "Collection note could not be saved. Backend PATCH /invoice/:id should be checked."
                ),
            );
        }
    };

    const resetFilters = () => {
        setSearch("");
        setCollectionStatus("all");
        setReturnStatus("all");
        setAgingBucket("all");
        setRiskScore("all");
        setStartDate(`${currentYear}-01-01`);
        setEndDate(todayValue);
        setSelectedParty(null);
        setTransportMode("all");
        setInvoiceOrigin("all");
        setCurrencyFilter("all");
        setGeographicScope("all");
    };

    const exportCsv = () => {
        const headers = [
            "Shipment",
            "Invoice No",
            "Customer",
            "Supplier",
            "Geographic Scope",
            "Transport Mode",
            "Invoice Origin",
            "Selling Currency",
            "Cost Currency",
            "Reporting Currency",
            "Email",
            "Gross Invoice Amount",
            "Issued Return Amount",
            "Net Invoice Amount",
            "Operational Cost",
            "Buying Rate",
            "Total Cost",
            "Profit",
            "Margin",
            "Paid",
            "Balance",
            "Customer Credit",
            "Due Date",
            "Aging",
            "Risk",
            "Collection Status",
            "Return Status",
            "Return Count",
            "Latest Return Invoice",
            "Promise To Pay",
            "Next Follow Up",
        ];

        const rows = filteredReceivables.map((item) => {
            const financials = getConvertedFinancials(item);
            const returnSummary = getReturnSummary(item);

            return [
                getShipmentNo(item),
                getInvoiceNo(item),
                getCustomerName(item),
                getSupplierName(item),
                getGeographicLabel(item, partyCountryLookup),
                getTransportMode(item),
                getInvoiceOrigin(item) === "manual" ? "Manual" : "TMS",
                getCurrency(item),
                getCostCurrency(item),
                reportingCurrency,
                item?.email || "",
                convertCurrency(
                    getInvoiceAmount(item),
                    getCurrency(item),
                    reportingCurrency,
                ) ?? "Rate unavailable",
                convertCurrency(
                    returnSummary.issuedAmount,
                    getCurrency(item),
                    reportingCurrency,
                ) ?? "Rate unavailable",
                financials.invoice ?? "Rate unavailable",
                convertCurrency(
                    getOperationalCost(item),
                    getCostCurrency(item),
                    reportingCurrency,
                ) ?? "Rate unavailable",
                convertCurrency(
                    getBuyingRate(item),
                    getCostCurrency(item),
                    reportingCurrency,
                ) ?? "Rate unavailable",
                financials.cost ?? "Rate unavailable",
                financials.profit ?? "Rate unavailable",
                financials.margin === null
                    ? "Rate unavailable"
                    : `${financials.margin.toFixed(2)}%`,
                convertCurrency(
                    getPaidAmount(item),
                    getCurrency(item),
                    reportingCurrency,
                ) ?? "Rate unavailable",
                convertCurrency(
                    getBalanceAmount(item),
                    getCurrency(item),
                    reportingCurrency,
                ) ?? "Rate unavailable",
                convertCurrency(
                    getCustomerCreditAmount(item),
                    getCurrency(item),
                    reportingCurrency,
                ) ?? "Rate unavailable",
                formatDate(getDueDate(item)),
                getAgingBucket(getDueDate(item), getCollectionStatus(item)),
                getRiskScore(item),
                getCollectionStatus(item),
                returnSummary.status,
                returnSummary.count,
                returnSummary.latestReturnInvoice
                    ? getReturnInvoiceNo(returnSummary.latestReturnInvoice)
                    : "",
                formatDate(item?.promiseToPayDate),
                formatDate(item?.nextFollowUpDate),
            ];
        });

        const csv = [headers, ...rows]
            .map((row) =>
                row
                    .map((value) => `"${String(value ?? "").replaceAll('"', '""')}"`)
                    .join(","),
            )
            .join("\n");

        const blob = new Blob([`\uFEFF${csv}`], {
            type: "text/csv;charset=utf-8;",
        });

        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");

        link.href = url;
        const modeName = transportMode === "all" ? "all-modes" : transportMode;
        const scopeName =
            geographicScope === "all" ? "all-scopes" : geographicScope.toLowerCase();
        link.setAttribute(
            "download",
            `receivables-management-${modeName}-${scopeName}-${todayValue}.csv`,
        );

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        URL.revokeObjectURL(url);
    };

    const tableData = useMemo(
        () => ({
            columns: [
                {
                    Header: financeText("CUSTOMER / INVOICE"),
                    accessor: "customerName",
                    width: "20%",
                    Cell: ({ row }) => {
                        const item = row.original;
                        const name = getCustomerName(item);

                        return (
                            <MDBox display="flex" alignItems="center" gap={1.1} py={0.35}>
                                <Avatar
                                    sx={{
                                        width: 36,
                                        height: 36,
                                        background: "#0B1F3A",
                                        color: "#FFFFFF",
                                        fontSize: "11px",
                                        fontWeight: 800,
                                        boxShadow: "none",
                                    }}
                                >
                                    {name.slice(0, 2).toUpperCase()}
                                </Avatar>

                                <MDBox minWidth={0}>
                                    <MDTypography
                                        variant="button"
                                        fontWeight="bold"
                                        noWrap
                                        sx={{
                                            cursor: "pointer",
                                            color: COLORS.text,
                                            display: "block",
                                            maxWidth: 165,
                                            "&:hover": {
                                                color: COLORS.blue,
                                            },
                                        }}
                                        onClick={() => openInvoiceDetails(item)}
                                    >
                                        {name}
                                    </MDTypography>

                                    <MDTypography
                                        variant="caption"
                                        noWrap
                                        display="block"
                                        sx={{ color: COLORS.subtext }}
                                    >
                                        {getInvoiceNo(item)} · {getShipmentNo(item)}
                                    </MDTypography>

                                    <MDBox display="flex" gap={0.5} mt={0.55}>
                                        <Chip
                                            size="small"
                                            label={financeText(getTransportMode(item))}
                                            sx={{
                                                height: 20,
                                                fontSize: "9px",
                                                fontWeight: 800,
                                                color: TRANSPORT_META[getTransportMode(item)].color,
                                                background:
                                                TRANSPORT_META[getTransportMode(item)].background,
                                            }}
                                        />
                                        <Chip
                                            size="small"
                                            label={getShipmentType(item)}
                                            sx={{
                                                height: 20,
                                                fontSize: "9px",
                                                fontWeight: 800,
                                                color: COLORS.navy,
                                                background: "#EEF2F6",
                                            }}
                                        />
                                    </MDBox>
                                </MDBox>
                            </MDBox>
                        );
                    },
                },
                {
                    Header: financeText("AMOUNTS"),
                    accessor: "amounts",
                    align: "right",
                    width: "16%",
                    Cell: ({ row }) => {
                        const item = row.original;
                        const currency = getCurrency(item);
                        const returnSummary = getReturnSummary(item);

                        return (
                            <MDBox textAlign="right">
                                <MDTypography
                                    variant="button"
                                    fontWeight="bold"
                                    sx={{ color: COLORS.amber }}
                                >
                                    {formatCompact(getBalanceAmount(item), currency)}
                                </MDTypography>

                                <MDTypography
                                    variant="caption"
                                    display="block"
                                    sx={{ color: COLORS.subtext }}
                                >
                                    {financeText("Gross")} {formatCompact(getInvoiceAmount(item), currency)}
                                </MDTypography>

                                {hasVatIncluded(item) && (
                                    <MDTypography
                                        variant="caption"
                                        display="block"
                                        sx={{ color: COLORS.subtext }}
                                    >
                                        {financeText("Incl. VAT")}{" "}
                                        {formatCompact(getVatAmount(item), currency)}
                                    </MDTypography>
                                )}

                                <MDTypography
                                    variant="caption"
                                    display="block"
                                    sx={{ color: COLORS.purple }}
                                >
                                    {financeText("Returned")} -
                                    {formatCompact(returnSummary.issuedAmount, currency)}
                                </MDTypography>

                                <MDTypography
                                    variant="caption"
                                    display="block"
                                    sx={{ color: COLORS.text }}
                                >
                                    {financeText("Net")} {formatCompact(getNetInvoiceAmount(item), currency)}
                                </MDTypography>

                                <MDTypography
                                    variant="caption"
                                    display="block"
                                    sx={{ color: COLORS.green }}
                                >
                                    {financeText("Paid")} {formatCompact(getPaidAmount(item), currency)}
                                </MDTypography>
                            </MDBox>
                        );
                    },
                },
                {
                    Header: financeText("PROFITABILITY"),
                    accessor: "actualProfit",
                    align: "right",
                    width: "13%",
                    Cell: ({ row }) => {
                        const item = row.original;
                        const financials = getConvertedFinancials(item);
                        const profit = financials.profit;
                        const margin = financials.margin;
                        const ratesAvailable = profit !== null;
                        const positive = profit >= 0;

                        return (
                            <MDBox textAlign="right">
                                <MDTypography
                                    variant="button"
                                    fontWeight="bold"
                                    sx={{
                                        color: !ratesAvailable
                                            ? COLORS.amber
                                            : positive
                                                ? COLORS.green
                                                : COLORS.red,
                                    }}
                                >
                                    {ratesAvailable
                                        ? formatCompact(profit, reportingCurrency)
                                        : financeText("Rate unavailable")}
                                </MDTypography>

                                <MDTypography
                                    variant="caption"
                                    display="block"
                                    sx={{
                                        color: positive ? COLORS.green : COLORS.red,
                                    }}
                                >
                                    {ratesAvailable
                                        ? `${margin.toFixed(1)}% ${financeText("margin")}`
                                        : financeText("Missing exchange rate")}
                                </MDTypography>

                                <MDTypography
                                    variant="caption"
                                    display="block"
                                    sx={{ color: COLORS.subtext }}
                                >
                                    {financeText("Cost")}{" "}
                                    {formatCompact(getTotalCost(item), getCostCurrency(item))}
                                </MDTypography>
                            </MDBox>
                        );
                    },
                },
                {
                    Header: financeText("DUE / AGING"),
                    accessor: "dueDate",
                    width: "16%",
                    Cell: ({ row }) => {
                        const item = row.original;

                        const bucket = getAgingBucket(
                            getDueDate(item),
                            getCollectionStatus(item),
                        );

                        const overdueDays = getOverdueDays(
                            getDueDate(item),
                            getCollectionStatus(item),
                        );

                        const isCritical = bucket === "61-90" || bucket === "90+";

                        return (
                            <MDBox>
                                <MDTypography
                                    variant="button"
                                    fontWeight="bold"
                                    sx={{
                                        color: overdueDays > 0 ? COLORS.red : COLORS.text,
                                    }}
                                >
                                    {formatDate(getDueDate(item))}
                                </MDTypography>

                                <MDTypography
                                    variant="caption"
                                    display="block"
                                    sx={{
                                        color: overdueDays > 0 ? COLORS.red : COLORS.green,
                                    }}
                                >
                                    {overdueDays > 0
                                        ? `${overdueDays} ${financeText("days overdue")}`
                                        : financeText("Within payment term")}
                                </MDTypography>

                                <Chip
                                    size="small"
                                    label={financeText(bucket)}
                                    sx={{
                                        mt: 0.5,
                                        height: 22,
                                        background:
                                            bucket === "Not Due"
                                                ? COLORS.greenLight
                                                : isCritical
                                                    ? COLORS.redLight
                                                    : COLORS.amberLight,
                                        color:
                                            bucket === "Not Due"
                                                ? COLORS.green
                                                : isCritical
                                                    ? COLORS.red
                                                    : COLORS.amber,
                                        fontWeight: 700,
                                        borderRadius: "7px",
                                    }}
                                />
                            </MDBox>
                        );
                    },
                },
                {
                    Header: financeText("RISK"),
                    accessor: "risk",
                    width: "9%",
                    Cell: ({ row }) => {
                        const score = getRiskScore(row.original);
                        const colors = getRiskColor(score);

                        return (
                            <Chip
                                size="small"
                                label={`${financeText("Risk")} ${score}`}
                                sx={{
                                    minWidth: 62,
                                    background: colors.bg,
                                    color: colors.color,
                                    fontWeight: 800,
                                    borderRadius: "8px",
                                }}
                            />
                        );
                    },
                },
                {
                    Header: financeText("FOLLOW-UP"),
                    accessor: "followUp",
                    width: "17%",
                    Cell: ({ row }) => (
                        <MDBox>
                            <MDBox display="flex" alignItems="center" gap={0.5}>
                                <CheckCircle
                                    sx={{
                                        fontSize: "14px !important",
                                        color: COLORS.purple,
                                    }}
                                />
                                <MDTypography
                                    variant="caption"
                                    fontWeight="bold"
                                    sx={{ color: COLORS.text }}
                                >
                                    {financeText("Promise")}: {formatDate(row.original?.promiseToPayDate) || "-"}
                                </MDTypography>
                            </MDBox>

                            <MDBox display="flex" alignItems="center" gap={0.5} mt={0.4}>
                                <EventAvailable
                                    sx={{
                                        fontSize: "14px !important",
                                        color: COLORS.blue,
                                    }}
                                />
                                <MDTypography variant="caption" sx={{ color: COLORS.subtext }}>
                                    {financeText("Next")}: {formatDate(row.original?.nextFollowUpDate) || "-"}
                                </MDTypography>
                            </MDBox>
                        </MDBox>
                    ),
                },
                {
                    Header: financeText("RETURN STATUS"),
                    accessor: "returnStatus",
                    width: "12%",
                    Cell: ({ row }) => {
                        const summary = getReturnSummary(row.original);
                        const style =
                            RETURN_STATUS_STYLES[summary.status] ||
                            RETURN_STATUS_STYLES["No Return"];

                        return (
                            <MDBox>
                                <Chip
                                    size="small"
                                    label={financeText(summary.status)}
                                    onClick={
                                        summary.latestReturnInvoice
                                            ? () => openLatestReturnInvoice(row.original)
                                            : undefined
                                    }
                                    sx={{
                                        height: 24,
                                        color: style.color,
                                        background: style.background,
                                        border: `1px solid ${style.border}`,
                                        fontWeight: 800,
                                        borderRadius: "8px",
                                        cursor: summary.latestReturnInvoice ? "pointer" : "default",
                                    }}
                                />
                                {summary.count > 0 && (
                                    <MDTypography
                                        variant="caption"
                                        display="block"
                                        mt={0.45}
                                        sx={{ color: COLORS.subtext }}
                                    >
                                        {summary.count}{" "}
                                        {financeText(
                                            summary.count === 1
                                                ? "return document"
                                                : "return documents"
                                        )}
                                    </MDTypography>
                                )}
                            </MDBox>
                        );
                    },
                },
                {
                    Header: financeText("STATUS"),
                    accessor: "collectionStatus",
                    width: "10%",
                    Cell: ({ row }) => (
                        <FinanceStatusChip status={getCollectionStatus(row.original)} />
                    ),
                },
                {
                    Header: financeText("ACTIONS"),
                    accessor: "actions",
                    align: "right",
                    width: "12%",
                    Cell: ({ row }) => (
                        <MDBox display="flex" justifyContent="flex-end" gap={0.5}>
                            <Tooltip title={financeText("View invoice")} arrow>
                                <IconButton
                                    size="small"
                                    onClick={() => openInvoiceDetails(row.original)}
                                    sx={{
                                        color: COLORS.blue,
                                        background: COLORS.blueLight,
                                        border: "1px solid #B2DDFF",
                                        "&:hover": {
                                            background: "#D1E9FF",
                                        },
                                    }}
                                >
                                    <Visibility sx={{ fontSize: "17px !important" }} />
                                </IconButton>
                            </Tooltip>

                            {getReturnSummary(row.original).latestReturnInvoice && (
                                <Tooltip title={financeText("View latest return invoice")} arrow>
                                    <IconButton
                                        size="small"
                                        onClick={() => openLatestReturnInvoice(row.original)}
                                        sx={{
                                            color: COLORS.purple,
                                            background: COLORS.purpleLight,
                                            border: "1px solid #D9D6FE",
                                            "&:hover": {
                                                background: "#E9E7FD",
                                            },
                                        }}
                                    >
                                        <RestartAlt sx={{ fontSize: "17px !important" }} />
                                    </IconButton>
                                </Tooltip>
                            )}

                            <Tooltip title={financeText("Add collection")} arrow>
                                <span style={{ display: "inline-flex" }}><IconButton
                                    size="small"
                                    onClick={() => addCollection(row.original)}
                                    disabled={getBalanceAmount(row.original) <= 0}
                                    sx={{
                                        color: COLORS.green,
                                        background: COLORS.greenLight,
                                        border: "1px solid #ABEFC6",
                                        "&:hover": {
                                            background: "#D1FADF",
                                        },
                                    }}
                                >
                                    <Paid sx={{ fontSize: "17px !important" }} />
                                </IconButton></span>
                            </Tooltip>

                            <Tooltip title={financeText("Send reminder")} arrow>
                                <IconButton
                                    size="small"
                                    onClick={() => sendReminder(row.original)}
                                    sx={{
                                        color: COLORS.purple,
                                        background: COLORS.purpleLight,
                                        border: "1px solid #D9D6FE",
                                        "&:hover": {
                                            background: "#E9E7FD",
                                        },
                                    }}
                                >
                                    <Email sx={{ fontSize: "17px !important" }} />
                                </IconButton>
                            </Tooltip>

                            <Tooltip title={financeText("Collection note")} arrow>
                                <IconButton
                                    size="small"
                                    onClick={() => openNoteModal(row.original)}
                                    sx={{
                                        color: COLORS.amber,
                                        background: COLORS.amberLight,
                                        border: "1px solid #FEDF89",
                                        "&:hover": {
                                            background: "#FEF0C7",
                                        },
                                    }}
                                >
                                    <Notes sx={{ fontSize: "17px !important" }} />
                                </IconButton>
                            </Tooltip>
                        </MDBox>
                    ),
                },
            ],
            rows: filteredReceivables,
        }),
        [filteredReceivables, getConvertedFinancials, reportingCurrency, financeLanguage],
    );

    const bucketMeta = {
        "Not Due": {
            color: COLORS.green,
            background: COLORS.greenLight,
        },
        "0-30": {
            color: COLORS.blue,
            background: COLORS.blueLight,
        },
        "31-60": {
            color: COLORS.amber,
            background: COLORS.amberLight,
        },
        "61-90": {
            color: "#C4320A",
            background: "#FFF4ED",
        },
        "90+": {
            color: COLORS.red,
            background: COLORS.redLight,
        },
    };

    return (
        <DashboardLayout>
            <DashboardNavbar />

            <MDBox
                pt={6}
                pb={3}
                sx={{
                    minHeight: "100vh",
                    background: COLORS.page,
                    mx: { xs: -2, md: -3 },
                    px: { xs: 2, md: 3 },
                }}
            >
                <Card
                    sx={{
                        borderRadius: "22px",
                        color: COLORS.white,
                        background:
                            "linear-gradient(125deg, #081A31 0%, #0D2D52 52%, #175CD3 135%)",
                        boxShadow: "0 18px 46px rgba(11, 31, 58, 0.22)",
                        overflow: "hidden",
                        position: "relative",
                        mb: 3,
                        "&::after": {
                            content: '""',
                            position: "absolute",
                            width: 360,
                            height: 360,
                            borderRadius: "50%",
                            right: -130,
                            top: -180,
                            background: "rgba(255,255,255,.07)",
                        },
                        "&::before": {
                            content: '""',
                            position: "absolute",
                            width: 230,
                            height: 230,
                            borderRadius: "50%",
                            right: 120,
                            bottom: -170,
                            background: "rgba(11,165,236,.16)",
                        },
                    }}
                >
                    <MDBox p={{ xs: 2.5, md: 3.5 }} position="relative" zIndex={1}>
                        <Grid container spacing={3} alignItems="center">
                            <Grid item xs={12} lg={7}>
                                <MDBox display="flex" alignItems="center" gap={1} mb={1}>
                                    <MDBox
                                        width={34}
                                        height={34}
                                        borderRadius="10px"
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="center"
                                        sx={{
                                            background: "rgba(255,255,255,.12)",
                                        }}
                                    >
                                        <WorkspacePremium
                                            sx={{
                                                color: "#FFFFFF",
                                                fontSize: 20,
                                            }}
                                        />
                                    </MDBox>

                                    <MDTypography
                                        variant="caption"
                                        fontWeight="bold"
                                        sx={{
                                            color: "rgba(255,255,255,.72)",
                                            letterSpacing: ".12em",
                                        }}
                                    >
                                        {financeText("CORPORATE RECEIVABLES CONTROL")}
                                    </MDTypography>
                                </MDBox>

                                <MDTypography
                                    variant="h2"
                                    fontWeight="bold"
                                    sx={{
                                        color: "#FFFFFF",
                                        lineHeight: 1.12,
                                    }}
                                >
                                    {financeText("Receivables Management")}
                                </MDTypography>

                                <MDTypography
                                    variant="body2"
                                    mt={1}
                                    sx={{
                                        color: "rgba(255,255,255,.72)",
                                        maxWidth: 720,
                                    }}
                                >
                                    {financeText(
                                        "Manage accounts receivable, aging, customer risk, sales returns, promise-to-pay commitments and collection actions from one banking-grade workspace."
                                    )}
                                </MDTypography>

                                <MDBox
                                    display="flex"
                                    alignItems="center"
                                    flexWrap="wrap"
                                    gap={1}
                                    mt={2.5}
                                >
                                    <Chip
                                        icon={
                                            <CheckCircle
                                                sx={{
                                                    color: "#75E0A7 !important",
                                                }}
                                            />
                                        }
                                        label={`${filteredReceivables.length} ${financeText("open receivables")}`}
                                        sx={{
                                            color: "#FFFFFF",
                                            background: "rgba(255,255,255,.10)",
                                            border: "1px solid rgba(255,255,255,.14)",
                                        }}
                                    />

                                    <Chip
                                        icon={
                                            <CalendarMonth
                                                sx={{
                                                    color: "#B2DDFF !important",
                                                }}
                                            />
                                        }
                                        label={`${financeText("Updated")} ${lastUpdated.toLocaleTimeString([], {
                                            hour: "2-digit",
                                            minute: "2-digit",
                                        })}`}
                                        sx={{
                                            color: "#FFFFFF",
                                            background: "rgba(255,255,255,.10)",
                                            border: "1px solid rgba(255,255,255,.14)",
                                        }}
                                    />
                                </MDBox>
                            </Grid>

                            <Grid item xs={12} lg={5}>
                                <MDBox
                                    p={2}
                                    borderRadius="16px"
                                    sx={{
                                        background: "rgba(255,255,255,.09)",
                                        border: "1px solid rgba(255,255,255,.13)",
                                        backdropFilter: "blur(8px)",
                                    }}
                                >
                                    <MDBox
                                        display="flex"
                                        justifyContent="space-between"
                                        alignItems="center"
                                        mb={1}
                                    >
                                        <MDTypography
                                            variant="caption"
                                            fontWeight="bold"
                                            sx={{
                                                color: "rgba(255,255,255,.72)",
                                            }}
                                        >
                                            {financeText("OUTSTANDING RECEIVABLES")}
                                        </MDTypography>

                                        <AccountBalanceWallet sx={{ color: "#FEDF89" }} />
                                    </MDBox>

                                    <MDTypography
                                        variant="h3"
                                        fontWeight="bold"
                                        sx={{ color: "#FFFFFF" }}
                                    >
                                        {formatCompact(stats.outstanding, reportingCurrency)}
                                    </MDTypography>

                                    <MDTypography
                                        variant="caption"
                                        sx={{
                                            color: "rgba(255,255,255,.68)",
                                        }}
                                    >
                                        {financeText("Collection rate")} {stats.collectionRate.toFixed(1)}% · DSO{" "}
                                        {stats.dso.toFixed(0)} {financeText("days")}
                                    </MDTypography>

                                    <MDBox display="flex" gap={1} mt={2} alignItems="stretch">
                                        <MDBox
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "4px",
                                                p: "4px",
                                                background: "rgba(255,255,255,.10)",
                                                border: "1px solid rgba(255,255,255,.20)",
                                                borderRadius: "12px",
                                                boxShadow: "inset 0 1px 2px rgba(0,0,0,.10)",
                                                flexShrink: 0,
                                            }}
                                        >
                                            <MDBox
                                                component="button"
                                                type="button"
                                                onClick={() =>
                                                    changeFinanceLanguage({
                                                        target: { value: "en" },
                                                    })
                                                }
                                                title="English"
                                                sx={{
                                                    appearance: "none",
                                                    WebkitAppearance: "none",
                                                    border: "none",
                                                    outline: "none",
                                                    cursor: "pointer",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    gap: "7px",
                                                    height: 38,
                                                    minWidth: 76,
                                                    px: 1.15,
                                                    borderRadius: "8px",
                                                    background:
                                                        financeLanguage === "en"
                                                            ? "#FFFFFF"
                                                            : "transparent",
                                                    color:
                                                        financeLanguage === "en"
                                                            ? COLORS.navy
                                                            : "#FFFFFF",
                                                    boxShadow:
                                                        financeLanguage === "en"
                                                            ? "0 3px 10px rgba(0,0,0,.18)"
                                                            : "none",
                                                }}
                                            >
                                                <MDBox
                                                    component="img"
                                                    src="/flags/gb.svg"
                                                    alt="English"
                                                    sx={{
                                                        width: 24,
                                                        height: 17,
                                                        objectFit: "cover",
                                                        borderRadius: "3px",
                                                        display: "block",
                                                    }}
                                                />
                                                <MDTypography
                                                    component="span"
                                                    sx={{
                                                        fontSize: "12px",
                                                        fontWeight: 800,
                                                        color: "inherit",
                                                        lineHeight: 1,
                                                    }}
                                                >
                                                    EN
                                                </MDTypography>
                                            </MDBox>

                                            <MDBox
                                                component="button"
                                                type="button"
                                                onClick={() =>
                                                    changeFinanceLanguage({
                                                        target: { value: "tr" },
                                                    })
                                                }
                                                title="Türkçe"
                                                sx={{
                                                    appearance: "none",
                                                    WebkitAppearance: "none",
                                                    border: "none",
                                                    outline: "none",
                                                    cursor: "pointer",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    gap: "7px",
                                                    height: 38,
                                                    minWidth: 76,
                                                    px: 1.15,
                                                    borderRadius: "8px",
                                                    background:
                                                        financeLanguage === "tr"
                                                            ? "#FFFFFF"
                                                            : "transparent",
                                                    color:
                                                        financeLanguage === "tr"
                                                            ? COLORS.navy
                                                            : "#FFFFFF",
                                                    boxShadow:
                                                        financeLanguage === "tr"
                                                            ? "0 3px 10px rgba(0,0,0,.18)"
                                                            : "none",
                                                }}
                                            >
                                                <MDBox
                                                    component="img"
                                                    src="/flags/tr.svg"
                                                    alt="Türkçe"
                                                    sx={{
                                                        width: 24,
                                                        height: 17,
                                                        objectFit: "cover",
                                                        borderRadius: "3px",
                                                        display: "block",
                                                    }}
                                                />
                                                <MDTypography
                                                    component="span"
                                                    sx={{
                                                        fontSize: "12px",
                                                        fontWeight: 800,
                                                        color: "inherit",
                                                        lineHeight: 1,
                                                    }}
                                                >
                                                    TR
                                                </MDTypography>
                                            </MDBox>
                                        </MDBox>

                                        <MDButton
                                            variant="contained"
                                            onClick={() => navigate("/invoice/finance/collections")}
                                            sx={{
                                                background: "#FFFFFF !important",
                                                color: `${COLORS.navy} !important`,
                                                flex: 1,
                                                boxShadow: "none",
                                            }}
                                        >
                                            <Paid sx={{ mr: 0.5 }} />
                                            {financeText("Collections")}
                                        </MDButton>

                                        <Tooltip title={financeText("Refresh receivables")}>
                                            <IconButton
                                                onClick={fetchReceivables}
                                                sx={{
                                                    color: "#FFFFFF",
                                                    border: "1px solid rgba(255,255,255,.18)",
                                                    borderRadius: "10px",
                                                }}
                                            >
                                                <Refresh />
                                            </IconButton>
                                        </Tooltip>
                                    </MDBox>
                                </MDBox>
                            </Grid>
                        </Grid>
                    </MDBox>
                </Card>

                <Card sx={{ ...cardSx, mb: 2.5 }}>
                    <MDBox p={{ xs: 2, md: 2.5 }}>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} sm={6} lg={2}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    type="date"
                                    label={financeText("Start Date")}
                                    value={startDate}
                                    onChange={(event) => setStartDate(event.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} lg={2}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    type="date"
                                    label={financeText("End Date")}
                                    value={endDate}
                                    onChange={(event) => setEndDate(event.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} lg={2}>
                                <TextField
                                    select
                                    fullWidth
                                    size="small"
                                    label={financeText("Customer / Supplier")}
                                    value={
                                        selectedParty
                                            ? `${selectedParty.type}-${selectedParty.name}`
                                            : "all"
                                    }
                                    onChange={(event) => {
                                        if (event.target.value === "all") {
                                            setSelectedParty(null);
                                            return;
                                        }

                                        setSelectedParty(
                                            partyOptions.find(
                                                (option) =>
                                                    `${option.type}-${option.name}` ===
                                                    event.target.value,
                                            ) || null,
                                        );
                                    }}
                                >
                                    <MenuItem value="all">{financeText("All Customers / Suppliers")}</MenuItem>
                                    {partyOptions.map((option) => {
                                        const value = `${option.type}-${option.name}`;
                                        return (
                                            <MenuItem key={value} value={value}>
                                                {option.label}
                                            </MenuItem>
                                        );
                                    })}
                                </TextField>
                            </Grid>

                            <Grid item xs={12} sm={6} lg={2}>
                                <TextField
                                    select
                                    fullWidth
                                    size="small"
                                    label={financeText("Invoice Origin")}
                                    value={invoiceOrigin}
                                    onChange={(event) => setInvoiceOrigin(event.target.value)}
                                >
                                    <MenuItem value="all">{financeText("All Origins")}</MenuItem>
                                    <MenuItem value="tms">TMS</MenuItem>
                                    <MenuItem value="manual">{financeText("Manual")}</MenuItem>
                                </TextField>
                            </Grid>

                            <Grid item xs={12} sm={6} lg={2}>
                                <TextField
                                    select
                                    fullWidth
                                    size="small"
                                    label={financeText("Geographic Scope")}
                                    value={geographicScope}
                                    onChange={(event) => setGeographicScope(event.target.value)}
                                >
                                    <MenuItem value="all">{financeText("All Geographic Scopes")}</MenuItem>
                                    <MenuItem value="DOMESTIC">{financeText("Domestic")}</MenuItem>
                                    <MenuItem value="INTERNATIONAL">{financeText("International")}</MenuItem>
                                </TextField>
                            </Grid>

                            <Grid item xs={12} sm={6} lg={2}>
                                <TextField
                                    select
                                    fullWidth
                                    size="small"
                                    label={financeText("Transport Mode")}
                                    value={transportMode}
                                    onChange={(event) => setTransportMode(event.target.value)}
                                >
                                    <MenuItem value="all">{financeText("All Transport Modes")}</MenuItem>
                                    {transportModeOptions.map((mode) => (
                                        <MenuItem key={mode} value={mode}>
                                            {financeText(mode)}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>

                            <Grid item xs={12} sm={6} lg={2}>
                                <TextField
                                    select
                                    fullWidth
                                    size="small"
                                    label={financeText("Currency")}
                                    value={currencyFilter}
                                    onChange={(event) => setCurrencyFilter(event.target.value)}
                                >
                                    <MenuItem value="all">{financeText("All Currencies")}</MenuItem>
                                    {currencyOptions.map((currency) => (
                                        <MenuItem key={currency} value={currency}>
                                            {currency}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>

                            {/* Display currency -- purely a presentation
                                choice: it converts the aggregated totals
                                below into this currency. It does NOT filter
                                any records (that is the "Currency" select
                                above). */}
                            <Grid item xs={12} sm={6} lg={2}>
                                <TextField
                                    select
                                    fullWidth
                                    size="small"
                                    label={financeText("Display Currency")}
                                    value={selectedCurrency}
                                    onChange={(event) => setSelectedCurrency(event.target.value)}
                                    helperText={financeText("Totals are shown in this currency")}
                                >
                                    {CURRENCY_OPTIONS.map((currency) => (
                                        <MenuItem key={currency} value={currency}>
                                            {currency}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                        </Grid>
                    </MDBox>
                </Card>

                {loading ? (
                    <Grid container spacing={SPACE.rowGap}>
                        <Grid item xs={12} lg={7}>
                            <Skeleton variant="rounded" height={260} sx={{ borderRadius: SPACE.radiusCard }} />
                        </Grid>
                        <Grid item xs={12} lg={5}>
                            <Skeleton variant="rounded" height={260} sx={{ borderRadius: SPACE.radiusCard }} />
                        </Grid>
                    </Grid>
                ) : (
                    <>
                        {/* Same hero + secondary-tiles pattern as the Finance
                            Dashboard, from the shared FinanceUiKit. */}

                        <Grid container spacing={SPACE.rowGap} mt={0.5}>
                            <Grid item xs={12} sm={6} lg={3}>
                                <FinanceKpiTile
                                    title={financeText("Open AR")}
                                    value={formatNumber(stats.count)}
                                    subtitle={financeText("Receivable records")}
                                    icon={<ReceiptLong />}
                                    tone="navy"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} lg={3}>
                                <FinanceKpiTile
                                    title={financeText("Issued Returns")}
                                    value={formatCompact(stats.returnedAmount, reportingCurrency)}
                                    subtitle={financeText("Finalized sales returns")}
                                    icon={<RestartAlt />}
                                    tone="purple"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} lg={3}>
                                <FinanceKpiTile
                                    title={financeText("Customer Credit")}
                                    value={formatCompact(stats.customerCreditAmount, reportingCurrency)}
                                    subtitle={financeText("Collected above net invoice")}
                                    icon={<AccountBalanceWallet />}
                                    tone="blue"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} lg={3}>
                                <FinanceKpiTile
                                    title={financeText("Buying Rate")}
                                    value={formatCompact(stats.buyingRate, reportingCurrency)}
                                    subtitle={financeText("Purchase cost included in total")}
                                    icon={<AccountBalanceWallet />}
                                    tone="amber"
                                />
                            </Grid>
                        </Grid>




                        <Card sx={{ ...cardSx, mt: 2.5 }}>
                            <MDBox p={3}>
                                <MDBox
                                    display="flex"
                                    alignItems={{ xs: "flex-start", md: "center" }}
                                    justifyContent="space-between"
                                    flexDirection={{ xs: "column", md: "row" }}
                                    gap={1.5}
                                    mb={2.5}
                                >
                                    <MDBox display="flex" alignItems="center" gap={1}>
                                        <MDBox
                                            width={38}
                                            height={38}
                                            borderRadius="11px"
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="center"
                                            sx={{
                                                color: COLORS.blue,
                                                background: COLORS.blueLight,
                                                border: "1px solid #B2DDFF",
                                            }}
                                        >
                                            <FilterAlt sx={{ fontSize: 19 }} />
                                        </MDBox>

                                        <MDBox>
                                            <MDTypography
                                                variant="h6"
                                                fontWeight="bold"
                                                sx={{ color: COLORS.text }}
                                            >
                                                {financeText("Receivable Filters")}
                                            </MDTypography>

                                            <MDTypography
                                                variant="caption"
                                                sx={{ color: COLORS.subtext }}
                                            >
                                                {financeText(
                                                    "Search and segment receivables by customer, collection status, return status, aging and risk."
                                                )}
                                            </MDTypography>
                                        </MDBox>
                                    </MDBox>

                                    <MDBox display="flex" flexWrap="wrap" gap={1}>
                                        <MDButton
                                            variant="outlined"
                                            color="dark"
                                            size="small"
                                            onClick={fetchReceivables}
                                            sx={{ borderRadius: "10px" }}
                                        >
                                            <Refresh sx={{ mr: 0.5 }} />
                                            {financeText("Refresh")}
                                        </MDButton>

                                        <MDButton
                                            variant="outlined"
                                            color="dark"
                                            size="small"
                                            onClick={resetFilters}
                                            sx={{ borderRadius: "10px" }}
                                        >
                                            <RestartAlt sx={{ mr: 0.5 }} />
                                            {financeText("Reset")}
                                        </MDButton>

                                        <MDButton
                                            variant="contained"
                                            size="small"
                                            onClick={exportCsv}
                                            sx={{
                                                borderRadius: "10px",
                                                background: `${COLORS.navy} !important`,
                                                color: "#FFFFFF !important",
                                                boxShadow: "none",
                                            }}
                                        >
                                            <Download sx={{ mr: 0.5 }} />
                                            {financeText("Export CSV")}
                                        </MDButton>

                                        <MDButton
                                            variant="outlined"
                                            color="dark"
                                            size="small"
                                            onClick={() =>
                                                navigate("/invoice/finance/customer-invoices")
                                            }
                                            sx={{ borderRadius: "10px" }}
                                        >
                                            {financeText("Customer Invoices")}
                                        </MDButton>
                                    </MDBox>
                                </MDBox>

                                <Grid container spacing={2}>
                                    <Grid item xs={12} lg={3}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label={financeText("Search")}
                                            placeholder={financeText("Shipment, invoice, customer...")}
                                            value={search}
                                            onChange={(event) => setSearch(event.target.value)}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <Search
                                                            fontSize="small"
                                                            sx={{ color: COLORS.subtext }}
                                                        />
                                                    </InputAdornment>
                                                ),
                                            }}
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    borderRadius: "11px",
                                                    background: "#FFFFFF",
                                                },
                                            }}
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={6} lg={3}>
                                        <TextField
                                            select
                                            fullWidth
                                            size="small"
                                            label={financeText("Collection Status")}
                                            value={collectionStatus}
                                            onChange={(event) =>
                                                setCollectionStatus(event.target.value)
                                            }
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    borderRadius: "11px",
                                                    background: "#FFFFFF",
                                                },
                                            }}
                                        >
                                            {statusOptions.map((item) => (
                                                <MenuItem key={item} value={item}>
                                                    {item === "all" ? financeText("All Collection Statuses") : financeText(item)}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid>

                                    <Grid item xs={12} sm={6} lg={3}>
                                        <TextField
                                            select
                                            fullWidth
                                            size="small"
                                            label={financeText("Return Status")}
                                            value={returnStatus}
                                            onChange={(event) => setReturnStatus(event.target.value)}
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    borderRadius: "11px",
                                                    background: "#FFFFFF",
                                                },
                                            }}
                                        >
                                            {returnStatusOptions.map((item) => (
                                                <MenuItem key={item} value={item}>
                                                    {item === "all" ? financeText("All Return Statuses") : financeText(item)}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid>

                                    <Grid item xs={12} sm={6} lg={3}>
                                        <TextField
                                            select
                                            fullWidth
                                            size="small"
                                            label={financeText("Aging Bucket")}
                                            value={agingBucket}
                                            onChange={(event) => setAgingBucket(event.target.value)}
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    borderRadius: "11px",
                                                    background: "#FFFFFF",
                                                },
                                            }}
                                        >
                                            <MenuItem value="all">{financeText("All Aging Buckets")}</MenuItem>
                                            <MenuItem value="Not Due">{financeText("Not Due")}</MenuItem>
                                            <MenuItem value="0-30">{financeText("0-30 Days")}</MenuItem>
                                            <MenuItem value="31-60">{financeText("31-60 Days")}</MenuItem>
                                            <MenuItem value="61-90">{financeText("61-90 Days")}</MenuItem>
                                            <MenuItem value="90+">{financeText("90+ Days")}</MenuItem>
                                        </TextField>
                                    </Grid>

                                    <Grid item xs={12} sm={6} lg={3}>
                                        <TextField
                                            select
                                            fullWidth
                                            size="small"
                                            label={financeText("Risk Score")}
                                            value={riskScore}
                                            onChange={(event) => setRiskScore(event.target.value)}
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    borderRadius: "11px",
                                                    background: "#FFFFFF",
                                                },
                                            }}
                                        >
                                            <MenuItem value="all">{financeText("All Risk Scores")}</MenuItem>
                                            <MenuItem value="A">{financeText("Risk")} A</MenuItem>
                                            <MenuItem value="B">{financeText("Risk")} B</MenuItem>
                                            <MenuItem value="C">{financeText("Risk")} C</MenuItem>
                                            <MenuItem value="D">{financeText("Risk")} D</MenuItem>
                                            <MenuItem value="E">{financeText("Risk")} E</MenuItem>
                                        </TextField>
                                    </Grid>
                                </Grid>

                                <MDBox
                                    mt={2.2}
                                    p={1.5}
                                    borderRadius="8px"
                                    display="flex"
                                    alignItems={{ xs: "flex-start", md: "center" }}
                                    justifyContent="space-between"
                                    flexDirection={{ xs: "column", md: "row" }}
                                    gap={1}
                                    sx={{
                                        background: "#F9FAFB",
                                        border: `1px solid ${COLORS.border}`,
                                    }}
                                >
                                    <MDTypography
                                        variant="caption"
                                        sx={{ color: COLORS.subtext }}
                                    >
                                        {financeText("Showing")}{" "}
                                        <strong style={{ color: COLORS.text }}>
                                            {filteredReceivables.length}
                                        </strong>{" "}
                                        {financeText("of")}{" "}
                                        <strong style={{ color: COLORS.text }}>
                                            {receivables.length}
                                        </strong>{" "}
                                        {financeText("receivable records")}
                                    </MDTypography>

                                    <MDBox display="flex" alignItems="center" gap={1}>
                                        <Chip
                                            size="small"
                                            label={`${financeText("Outstanding")} ${formatCompact(stats.outstanding, reportingCurrency)}`}
                                            sx={{
                                                color: COLORS.amber,
                                                background: COLORS.amberLight,
                                                fontWeight: 700,
                                            }}
                                        />

                                        <Chip
                                            size="small"
                                            label={`${financeText("Cost")} ${formatCompact(stats.totalCost, reportingCurrency)}`}
                                            sx={{
                                                color: COLORS.red,
                                                background: COLORS.redLight,
                                                fontWeight: 700,
                                            }}
                                        />

                                        <Chip
                                            size="small"
                                            label={`${financeText("Returns")} ${formatCompact(stats.returnedAmount, reportingCurrency)}`}
                                            sx={{
                                                color: COLORS.purple,
                                                background: COLORS.purpleLight,
                                                fontWeight: 700,
                                            }}
                                        />

                                        <Chip
                                            size="small"
                                            label={`${financeText("Profit")} ${formatCompact(stats.profit, reportingCurrency)}`}
                                            sx={{
                                                color: stats.profit >= 0 ? COLORS.green : COLORS.red,
                                                background:
                                                    stats.profit >= 0
                                                        ? COLORS.greenLight
                                                        : COLORS.redLight,
                                                fontWeight: 700,
                                            }}
                                        />

                                        <Chip
                                            size="small"
                                            label={`${financeText("Overdue")} ${formatCompact(stats.overdueAmount, reportingCurrency)}`}
                                            sx={{
                                                color: COLORS.red,
                                                background: COLORS.redLight,
                                                fontWeight: 700,
                                            }}
                                        />
                                    </MDBox>
                                </MDBox>

                                <Divider sx={{ my: 2.5 }} />

                                <MDBox
                                    sx={{
                                        "& .MuiTableContainer-root": {
                                            border: `1px solid ${COLORS.border}`,
                                            borderRadius: "10px",
                                            overflowX: "auto",
                                            boxShadow: "none",
                                        },
                                        "& table": {
                                            minWidth: 1580,
                                            borderCollapse: "separate",
                                            borderSpacing: 0,
                                        },
                                        "& table thead": {
                                            background:
                                                "#F8FAFC",
                                        },
                                        "& table thead th": {
                                            background: "transparent",
                                            color: "#475467 !important",
                                            fontSize: "10px !important",
                                            fontWeight: "800 !important",
                                            letterSpacing: ".085em",
                                            paddingTop: "15px !important",
                                            paddingBottom: "15px !important",
                                            borderBottom: `1px solid ${COLORS.border} !important`,
                                        },
                                        "& table tbody td": {
                                            paddingTop: "14px !important",
                                            paddingBottom: "14px !important",
                                            borderBottom: "1px solid #F0F2F5 !important",
                                            verticalAlign: "middle",
                                        },
                                        "& table tbody tr": {
                                            background: "#FFFFFF",
                                            transition: "background .18s ease, box-shadow .18s ease",
                                        },
                                        "& table tbody tr:nth-of-type(even)": {
                                            background: "#FCFCFD",
                                        },
                                        "& table tbody tr:hover": {
                                            background: "#F5F8FF !important",
                                            boxShadow: "inset 3px 0 0 #175CD3",
                                        },
                                        "& table tbody tr:last-child td": {
                                            borderBottom: "0 !important",
                                        },
                                        "& .MuiTablePagination-root": {
                                            borderTop: `1px solid ${COLORS.border}`,
                                            background: "#FCFCFD",
                                        },
                                    }}
                                >
                                    <DataTable
                                        table={tableData}
                                        canSearch={false}
                                        entriesPerPage
                                        showTotalEntries
                                        isSorted
                                        noEndBorder
                                    />
                                </MDBox>
                            </MDBox>
                        </Card>

                        {/* İkincil grafik ve kırılımlar: varsayılan kapalı, üstteki filtrelere bağlı */}
                        <FinanceDetailsSection
                            storageKey="receivables"
                            title={financeText("Detailed analysis")}
                            subtitle={financeText("Charts and breakdowns for the current filters")}
                        >
                            <Grid container spacing={SPACE.rowGap}>
                            <Grid item xs={12} lg={7}>
                                <FinanceHeroStat
                                    label={financeText("Gross Profit")}
                                    value={formatCompact(stats.profit, reportingCurrency)}
                                    tone={stats.profit >= 0 ? "green" : "red"}
                                    icon={<ArrowUpward />}
                                    meta={`${financeText("Margin")} ${stats.margin.toFixed(2)}%`}
                                    context={[
                                        { label: financeText("Outstanding"), value: formatCompact(stats.outstanding, reportingCurrency) },
                                        { label: financeText("Total Cost"), value: formatCompact(stats.totalCost, reportingCurrency) },
                                        { label: financeText("Collection Rate"), value: `${stats.collectionRate.toFixed(1)}%` },
                                    ]}
                                />
                            </Grid>

                            <Grid item xs={12} lg={5}>
                                <FinanceCard>
                                    <MDBox p={SPACE.cardPaddingLg} display="flex" flexDirection="column" height="100%">
                                        <FinanceSectionHeader
                                            title={getFinanceLanguage() === "tr" ? "Dikkat gerektiren" : "Needs attention"}
                                        />
                                        <MDBox display="flex" flexDirection="column" gap={1.25}>
                                            {[
                                                { label: financeText("Overdue"), value: formatCompact(stats.overdueAmount, reportingCurrency), sub: financeText("Past due exposure"), tone: "red" },
                                                { label: financeText("Critical Risk"), value: formatNumber(stats.criticalCount), sub: financeText("Risk D / E customers"), tone: "purple" },
                                                { label: financeText("DSO"), value: `${stats.dso.toFixed(0)} ${financeText("days")}`, sub: financeText("Weighted overdue days"), tone: "amber" },
                                            ].map((row) => (
                                                <MDBox
                                                    key={row.label}
                                                    display="flex"
                                                    alignItems="center"
                                                    justifyContent="space-between"
                                                    px={1.75}
                                                    py={1.25}
                                                    borderRadius={SPACE.radiusTile}
                                                    sx={{ background: COLORS.muted }}
                                                >
                                                    <MDBox display="flex" alignItems="center" gap={1.5} minWidth={0}>
                                                        <MDBox width={8} height={8} borderRadius="50%" flexShrink={0} sx={{ background: COLORS[row.tone] }} />
                                                        <MDBox minWidth={0}>
                                                            <MDTypography variant="button" fontWeight="medium" sx={{ color: COLORS.text, display: "block" }}>
                                                                {row.label}
                                                            </MDTypography>
                                                            <MDTypography variant="caption" sx={{ color: COLORS.subtext }}>
                                                                {row.sub}
                                                            </MDTypography>
                                                        </MDBox>
                                                    </MDBox>
                                                    <MDTypography variant="button" fontWeight="bold" sx={{ color: COLORS[row.tone], flexShrink: 0 }}>
                                                        {row.value}
                                                    </MDTypography>
                                                </MDBox>
                                            ))}
                                        </MDBox>
                                    </MDBox>
                                </FinanceCard>
                            </Grid>
                        </Grid>

                            <Grid container spacing={2.5} mt={0.1}>
                            {["Ocean", "Road", "Air"].map((mode) => (
                                <Grid item xs={12} lg={4} key={mode}>
                                    <ModeReceivableCard
                                        mode={mode}
                                        data={transportAnalytics[mode]}
                                        currency={reportingCurrency}
                                    />
                                </Grid>
                            ))}
                        </Grid>

                            <Grid container spacing={2.5} mt={0.1}>
                            <Grid item xs={12} lg={7}>
                                <Card sx={{ ...cardSx, height: "100%" }}>
                                    <MDBox p={3}>
                                        <MDBox
                                            display="flex"
                                            justifyContent="space-between"
                                            alignItems="center"
                                            mb={2.4}
                                        >
                                            <MDBox>
                                                <MDTypography
                                                    variant="h6"
                                                    fontWeight="bold"
                                                    sx={{ color: COLORS.text }}
                                                >
                                                    {financeText("Receivables by Transport Mode")}
                                                </MDTypography>
                                                <MDTypography
                                                    variant="caption"
                                                    sx={{ color: COLORS.subtext }}
                                                >
                                                    {financeText(
                                                        "Outstanding and overdue exposure across Ocean, Road and Air operations."
                                                    )}
                                                </MDTypography>
                                            </MDBox>
                                            <Chip
                                                size="small"
                                                label={`${filteredReceivables.length} ${financeText("records")}`}
                                                sx={{
                                                    color: COLORS.blue,
                                                    background: COLORS.blueLight,
                                                    fontWeight: 800,
                                                }}
                                            />
                                        </MDBox>

                                        {["Ocean", "Road", "Air"].map((mode) => {
                                            const data = transportAnalytics[mode];
                                            const meta = TRANSPORT_META[mode];
                                            const max = Math.max(
                                                transportAnalytics.Ocean.outstanding,
                                                transportAnalytics.Road.outstanding,
                                                transportAnalytics.Air.outstanding,
                                                1,
                                            );
                                            const percent = (data.outstanding / max) * 100;

                                            return (
                                                <MDBox key={mode} mb={2.1}>
                                                    <MDBox
                                                        display="flex"
                                                        justifyContent="space-between"
                                                        alignItems="center"
                                                        mb={0.8}
                                                    >
                                                        <MDBox display="flex" alignItems="center" gap={0.8}>
                                                            <MDBox
                                                                width={9}
                                                                height={9}
                                                                borderRadius="50%"
                                                                sx={{ background: meta.color }}
                                                            />
                                                            <MDTypography
                                                                variant="button"
                                                                fontWeight="bold"
                                                                sx={{ color: COLORS.text }}
                                                            >
                                                                {financeText(mode)}
                                                            </MDTypography>
                                                        </MDBox>
                                                        <MDBox textAlign="right">
                                                            <MDTypography
                                                                variant="button"
                                                                fontWeight="bold"
                                                                sx={{ color: COLORS.amber }}
                                                            >
                                                                {formatCompact(data.outstanding, reportingCurrency)}
                                                            </MDTypography>
                                                            <MDTypography
                                                                variant="caption"
                                                                display="block"
                                                                sx={{ color: COLORS.red }}
                                                            >
                                                                {financeText("Overdue")} {formatCompact(data.overdue, reportingCurrency)}
                                                            </MDTypography>
                                                        </MDBox>
                                                    </MDBox>
                                                    <LinearProgress
                                                        variant="determinate"
                                                        value={percent}
                                                        sx={{
                                                            height: 10,
                                                            borderRadius: "999px",
                                                            backgroundColor: "#F2F4F7",
                                                            "& .MuiLinearProgress-bar": {
                                                                borderRadius: "999px",
                                                                background: meta.color,
                                                            },
                                                        }}
                                                    />
                                                </MDBox>
                                            );
                                        })}
                                    </MDBox>
                                </Card>
                            </Grid>

                            <Grid item xs={12} lg={5}>
                                <Card sx={{ ...cardSx, height: "100%" }}>
                                    <MDBox p={3}>
                                        <MDTypography
                                            variant="h6"
                                            fontWeight="bold"
                                            sx={{ color: COLORS.text }}
                                        >
                                            {financeText("Shipment Type Mix")}
                                        </MDTypography>
                                        <MDTypography
                                            variant="caption"
                                            sx={{ color: COLORS.subtext }}
                                        >
                                            {financeText("Open receivables grouped by shipment and service type.")}
                                        </MDTypography>

                                        <MDBox mt={2.2}>
                                            {shipmentTypeAnalytics.length ? (
                                                shipmentTypeAnalytics.slice(0, 7).map((item) => {
                                                    const percent = stats.outstanding
                                                        ? (item.outstanding / stats.outstanding) * 100
                                                        : 0;

                                                    return (
                                                        <MDBox key={item.type} mb={1.6}>
                                                            <MDBox
                                                                display="flex"
                                                                justifyContent="space-between"
                                                                alignItems="center"
                                                                mb={0.55}
                                                            >
                                                                <MDBox
                                                                    display="flex"
                                                                    alignItems="center"
                                                                    gap={0.8}
                                                                >
                                                                    <Chip
                                                                        size="small"
                                                                        label={item.type}
                                                                        sx={{
                                                                            height: 22,
                                                                            color: COLORS.navy,
                                                                            background: "#EEF2F6",
                                                                            fontWeight: 800,
                                                                        }}
                                                                    />
                                                                    <MDTypography
                                                                        variant="caption"
                                                                        sx={{ color: COLORS.subtext }}
                                                                    >
                                                                        {item.count} {financeText("records")}
                                                                    </MDTypography>
                                                                </MDBox>
                                                                <MDTypography
                                                                    variant="caption"
                                                                    fontWeight="bold"
                                                                    sx={{ color: COLORS.text }}
                                                                >
                                                                    {formatCompact(item.outstanding, reportingCurrency)}
                                                                </MDTypography>
                                                            </MDBox>
                                                            <LinearProgress
                                                                variant="determinate"
                                                                value={Math.min(percent, 100)}
                                                                sx={{
                                                                    height: 6,
                                                                    borderRadius: "999px",
                                                                    backgroundColor: "#F2F4F7",
                                                                    "& .MuiLinearProgress-bar": {
                                                                        borderRadius: "999px",
                                                                        background: COLORS.navy,
                                                                    },
                                                                }}
                                                            />
                                                        </MDBox>
                                                    );
                                                })
                                            ) : (
                                                <MDTypography
                                                    variant="caption"
                                                    sx={{ color: COLORS.subtext }}
                                                >
                                                    {financeText("No shipment type data available.")}
                                                </MDTypography>
                                            )}
                                        </MDBox>
                                    </MDBox>
                                </Card>
                            </Grid>
                        </Grid>

                            <Card sx={{ ...cardSx, mt: 2.5 }}>
                            <MDBox p={3}>
                                <MDBox
                                    display="flex"
                                    justifyContent="space-between"
                                    alignItems={{ xs: "flex-start", md: "center" }}
                                    flexDirection={{ xs: "column", md: "row" }}
                                    gap={1.5}
                                    mb={2.5}
                                >
                                    <MDBox>
                                        <MDTypography
                                            variant="h6"
                                            fontWeight="bold"
                                            sx={{ color: COLORS.text }}
                                        >
                                            {financeText("Aging Distribution")}
                                        </MDTypography>

                                        <MDTypography
                                            variant="caption"
                                            sx={{ color: COLORS.subtext }}
                                        >
                                            {financeText("Outstanding customer balances by maturity bucket.")}
                                        </MDTypography>
                                    </MDBox>

                                    <Chip
                                        size="small"
                                        label={`${financeText("Total")} ${formatCompact(stats.outstanding, reportingCurrency)}`}
                                        sx={{
                                            color: COLORS.navy,
                                            background: "#EEF2F6",
                                            fontWeight: 700,
                                        }}
                                    />
                                </MDBox>

                                <Grid container spacing={2}>
                                    {Object.entries(stats.buckets).map(([bucket, amount]) => {
                                        const percent = stats.outstanding
                                            ? Math.min((amount / stats.outstanding) * 100, 100)
                                            : 0;

                                        const meta = bucketMeta[bucket];

                                        return (
                                            <Grid item xs={12} sm={6} lg={2.4} key={financeText(bucket)}>
                                                <Card
                                                    sx={{
                                                        borderRadius: "15px",
                                                        border: `1px solid ${COLORS.border}`,
                                                        boxShadow: "none",
                                                        height: "100%",
                                                        transition:
                                                            "transform .2s ease, border-color .2s ease",
                                                        "&:hover": {
                                                            transform: "none",
                                                            borderColor: meta.color,
                                                        },
                                                    }}
                                                >
                                                    <MDBox p={2}>
                                                        <MDBox
                                                            display="flex"
                                                            justifyContent="space-between"
                                                            alignItems="center"
                                                            mb={1}
                                                        >
                                                            <MDTypography
                                                                variant="button"
                                                                fontWeight="bold"
                                                                sx={{ color: COLORS.text }}
                                                            >
                                                                {financeText(bucket)}
                                                            </MDTypography>

                                                            <Chip
                                                                size="small"
                                                                label={`${percent.toFixed(1)}%`}
                                                                sx={{
                                                                    height: 22,
                                                                    color: meta.color,
                                                                    background: meta.background,
                                                                    fontWeight: 700,
                                                                }}
                                                            />
                                                        </MDBox>

                                                        <MDTypography
                                                            variant="h5"
                                                            fontWeight="bold"
                                                            sx={{ color: meta.color }}
                                                        >
                                                            {formatCompact(amount, reportingCurrency)}
                                                        </MDTypography>

                                                        <LinearProgress
                                                            variant="determinate"
                                                            value={percent}
                                                            sx={{
                                                                height: 7,
                                                                borderRadius: "999px",
                                                                mt: 1.4,
                                                                backgroundColor: "#F2F4F7",
                                                                "& .MuiLinearProgress-bar": {
                                                                    borderRadius: "999px",
                                                                    background: meta.color,
                                                                },
                                                            }}
                                                        />
                                                    </MDBox>
                                                </Card>
                                            </Grid>
                                        );
                                    })}
                                </Grid>
                            </MDBox>
                        </Card>
                        </FinanceDetailsSection>

                    </>
                )}
            </MDBox>

            <Dialog
                open={noteModalOpen}
                onClose={() => setNoteModalOpen(false)}
                fullWidth
                maxWidth="sm"
                PaperProps={{
                    sx: {
                        borderRadius: "10px",
                        overflow: "hidden",
                        border: `1px solid ${COLORS.border}`,
                        boxShadow: "0 24px 64px rgba(16,24,40,.18)",
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        px: 3,
                        py: 2.5,
                        background:
                            "#0B1F3A",
                        color: "#FFFFFF",
                    }}
                >
                    <MDBox display="flex" alignItems="center" gap={1.2}>
                        <MDBox
                            width={38}
                            height={38}
                            borderRadius="11px"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            sx={{
                                background: "rgba(255,255,255,.12)",
                            }}
                        >
                            <Notes />
                        </MDBox>

                        <MDBox>
                            <MDTypography
                                variant="h6"
                                fontWeight="bold"
                                sx={{ color: "#FFFFFF" }}
                            >
                                {financeText("Collection Follow-up")}
                            </MDTypography>

                            <MDTypography
                                variant="caption"
                                sx={{
                                    color: "rgba(255,255,255,.68)",
                                }}
                            >
                                {financeText("Register promise-to-pay and next collection action.")}
                            </MDTypography>
                        </MDBox>
                    </MDBox>
                </DialogTitle>

                <DialogContent sx={{ p: 3 }}>
                    {selectedReceivable && (
                        <Card
                            sx={{
                                ...cardSx,
                                mb: 2.5,
                                background: "#F8FAFC",
                            }}
                        >
                            <MDBox p={2}>
                                <MDBox
                                    display="flex"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    gap={1}
                                >
                                    <MDBox>
                                        <MDTypography
                                            variant="button"
                                            fontWeight="bold"
                                            sx={{ color: COLORS.text }}
                                        >
                                            {getCustomerName(selectedReceivable)}
                                        </MDTypography>

                                        <MDTypography
                                            variant="caption"
                                            display="block"
                                            sx={{ color: COLORS.subtext }}
                                        >
                                            {getInvoiceNo(selectedReceivable)}
                                        </MDTypography>
                                    </MDBox>

                                    <Chip
                                        size="small"
                                        label={`${financeText("Balance")} ${formatCompact(
                                            getBalanceAmount(selectedReceivable),
                                            getCurrency(selectedReceivable),
                                        )}`}
                                        sx={{
                                            color: COLORS.red,
                                            background: COLORS.redLight,
                                            fontWeight: 700,
                                        }}
                                    />
                                </MDBox>
                            </MDBox>
                        </Card>
                    )}

                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                size="small"
                                type="date"
                                label={financeText("Promise To Pay")}
                                InputLabelProps={{ shrink: true }}
                                value={noteForm.promiseToPayDate}
                                onChange={(event) =>
                                    setNoteForm((previous) => ({
                                        ...previous,
                                        promiseToPayDate: event.target.value,
                                    }))
                                }
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: "11px",
                                    },
                                }}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                size="small"
                                type="date"
                                label={financeText("Next Follow-up")}
                                InputLabelProps={{ shrink: true }}
                                value={noteForm.nextFollowUpDate}
                                onChange={(event) =>
                                    setNoteForm((previous) => ({
                                        ...previous,
                                        nextFollowUpDate: event.target.value,
                                    }))
                                }
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: "11px",
                                    },
                                }}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                size="small"
                                multiline
                                rows={4}
                                label={financeText("Collection Note")}
                                value={noteForm.collectionNote}
                                onChange={(event) =>
                                    setNoteForm((previous) => ({
                                        ...previous,
                                        collectionNote: event.target.value,
                                    }))
                                }
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: "11px",
                                    },
                                }}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>

                <DialogActions
                    sx={{
                        px: 3,
                        py: 2.2,
                        background: "#F9FAFB",
                        borderTop: `1px solid ${COLORS.border}`,
                    }}
                >
                    <MDButton
                        variant="outlined"
                        color="dark"
                        onClick={() => setNoteModalOpen(false)}
                        sx={{ borderRadius: "10px" }}
                    >
                        {financeText("Cancel")}
                    </MDButton>

                    <MDButton
                        variant="contained"
                        onClick={saveNote}
                        sx={{
                            borderRadius: "10px",
                            background: `${COLORS.navy} !important`,
                            color: "#FFFFFF !important",
                            boxShadow: "none",
                        }}
                    >
                        {financeText("Save Follow-up")}
                    </MDButton>
                </DialogActions>
            </Dialog>

            <Footer />
        </DashboardLayout>
    );
};

export default Receivables;